import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Modal, Form, Row, Col, Table, Pagination, InputGroup, Collapse, Spinner } from 'react-bootstrap';
import ClientSearchModal from './ClientSearchModal';
import RubroSearchModal from './RubroSearchModal';
import ArticleSearchModal from './ArticleSearchModal';
import GlobalArticleSearchModal from './GlobalArticleSearchModal';
import ArticleImageModal from './ArticleImageModal';
import QuoteSuccessModal from './QuoteSuccessModal';
import Swal from 'sweetalert2';

const Quoter = ({ user }) => {


    // --- ESTADOS DEL COTIZADOR (LA GRILLA DE PRODUCTOS) ---
    const [rubros, setRubros] = useState([]);               // Lista de rubros cargados lateralmente
    const [isRubrosCollapsed, setIsRubrosCollapsed] = useState(true);
    const [selectedRubro, setSelectedRubro] = useState(null); // Rubro activo seleccionado
    const [availableArticles, setAvailableArticles] = useState([]); // Artículos del rubro seleccionado
    const [rubroArticlesPage, setRubroArticlesPage] = useState(1);
    const [hasMoreRubroArticles, setHasMoreRubroArticles] = useState(true);
    const [isLoadingMoreRubroArticles, setIsLoadingMoreRubroArticles] = useState(false);
    const [quotedItems, setQuotedItems] = useState([]);      // EL CORAZÓN: Lista de ítems cargados en la factura
    const [selectedQuotedItemIndex, setSelectedQuotedItemIndex] = useState(null); // Fila resaltada actualmente
    const [dbClients, setDbClients] = useState([]);          // Client DB records fetched from Backend
    const [dbSellers, setDbSellers] = useState([]);          // Sellers DB records fetched from Backend
    const [dbLists, setDbLists] = useState([]);              // Price lists fetched from Backend
    const [isLoadingClients, setIsLoadingClients] = useState(true);
    const [isLoadingLists, setIsLoadingLists] = useState(true);
    const [isLoadingRubros, setIsLoadingRubros] = useState(true);
    const [isLoadingArticles, setIsLoadingArticles] = useState(false);
    const [pricesByArtic, setPricesByArtic] = useState({}); // Precios de la lista actual { c_artic: i_lista }
    const [rawGlobalArticles, setRawGlobalArticles] = useState([]);
    const [isGlobalLoading, setIsGlobalLoading] = useState(false);

    // --- ESTADOS DE LA CABECERA (DATOS DEL CLIENTE Y VENTA) ---
    const [isHeaderConfirmed, setIsHeaderConfirmed] = useState(false); // ¿La cabecera está "Aceptada"?
    const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);   // Controla si se ve o se oculta la cabecera
    const [showClientModal, setShowClientModal] = useState(false);     // Modal de búsqueda de clientes
    const [showListModal, setShowListModal] = useState(false);         // Modal de búsqueda de listas de precio
    const [showRubroModal, setShowRubroModal] = useState(false);       // Modal de búsqueda de rubros
    const [showArticleModal, setShowArticleModal] = useState(false);   // Modal de búsqueda de artículos por rubro
    const [showGlobalArticleModal, setShowGlobalArticleModal] = useState(false); // Buscador general de artículos
    const [globalSearchTerm, setGlobalSearchTerm] = useState('');     // Término de búsqueda para el modal global
    const [showGeneralNoteModal, setShowGeneralNoteModal] = useState(false); // Nota que va al pie del presupuesto
    const [generalNote, setGeneralNote] = useState('');
    const [showDeliveryAddressModal, setShowDeliveryAddressModal] = useState(false);
    const [deliveryAddressOption, setDeliveryAddressOption] = useState('fiscal'); // 'fiscal' | 'alt1' | 'alt2' | 'manual'
    const [manualDeliveryAddress, setManualDeliveryAddress] = useState('');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [tempDeliveryOption, setTempDeliveryOption] = useState('fiscal');
    const [tempManualAddress, setTempManualAddress] = useState('');
    const [isClientLocked, setIsClientLocked] = useState(false);       // Bloquea campos del cliente una vez confirmados
    const [showCancelModal, setShowCancelModal] = useState(false);     // Confirmación para resetear todo

    // --- ESTADOS DE INTERFAZ ---
    const [activeRowIndex, setActiveRowIndex] = useState(null);
    const [focusedInput, setFocusedInput] = useState(null);
    const selectTimerRef = React.useRef(null);
    const scannerStartTimeRef = React.useRef({}); // Timestamp del último char por fila, para detectar pistola
    const idValueRef = React.useRef({});           // Valor actual del campo id por fila (sincrónico, sin depender del estado React)
    const quotedItemsRef = React.useRef([]);
    quotedItemsRef.current = quotedItems;

    const handleSafeFocus = (inputId, rowIndex = null, e = null) => {
        setFocusedInput(inputId);
        if (rowIndex !== null && rowIndex !== undefined) {
            setSelectedQuotedItemIndex(rowIndex);
        }
        if (selectTimerRef.current) {
            clearTimeout(selectTimerRef.current);
            selectTimerRef.current = null;
        }
        const target = e?.target;
        if (target) {
            selectTimerRef.current = setTimeout(() => {
                if (document.activeElement === target && typeof target.select === 'function') {
                    target.select();
                }
            }, 20);
        }
    };

    const handleSafeBlur = (callback) => {
        if (selectTimerRef.current) {
            clearTimeout(selectTimerRef.current);
            selectTimerRef.current = null;
        }
        setFocusedInput(null);
        if (typeof callback === 'function') {
            callback();
        }
    };

    const [showLiteralModal, setShowLiteralModal] = useState(false);
    const [literalItem, setLiteralItem] = useState({ id: '', description: '', price: '', quantity: 1, isExtended: false });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showFinalConfirmModal, setShowFinalConfirmModal] = useState(false);
    const [showSaveDraftModal, setShowSaveDraftModal] = useState(false);
    const [editingDraftId, setEditingDraftId] = useState(null);
    const [editingDraftLabel, setEditingDraftLabel] = useState('');
    const location = useLocation();
    const navigate = useNavigate();
    const loadedDraftKeyRef = React.useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [savedQuoteData, setSavedQuoteData] = useState(null);
    const [showDeleteRowModal, setShowDeleteRowModal] = useState(false);
    const [rowToDeleteIndex, setRowToDeleteIndex] = useState(null);
    const [unassignedClientError, setUnassignedClientError] = useState(false);

    // Estado para modal de imágenes de producto en versión mobile
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageArticle, setSelectedImageArticle] = useState(null);

    const handleOpenImageModal = (article) => {
        if (!article) return;
        setSelectedImageArticle(article);
        setShowImageModal(true);
    };

    const handleOpenImageModalForSelected = () => {
        if (selectedQuotedItemIndex !== null && quotedItems[selectedQuotedItemIndex]) {
            const item = quotedItems[selectedQuotedItemIndex];
            if (item.id || item.name) {
                handleOpenImageModal(item);
            }
        }
    };

    const [listSearch, setListSearch] = useState('');
    const [listPage, setListPage] = useState(1);
    const [paymentCondition, setPaymentCondition] = useState('');
    const [paymentConditions, setPaymentConditions] = useState([]);
    const [ivaConditions, setIvaConditions] = useState([]);

    // Estados iniciales para resetear fácilmente
    const initialClientState = { id: '', name: '', address: '', d_entre1: '', d_entre2: '', vatStatus: '' };
    const initialListState = { id: '', description: '' };

    const [client, setClient] = useState(initialClientState);
    const [list, setList] = useState(initialListState);

    // Efecto para asegurar que el rubro seleccionado sea visible en el sidebar
    React.useEffect(() => {
        if (selectedRubro) {
            const rubroElement = document.getElementById(`rubro-btn-${selectedRubro.c_rubro}`);
            if (rubroElement) {
                rubroElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [selectedRubro]);

    // Efecto para bloquear de forma permanente la edición del artículo en filas completadas
    useEffect(() => {
        setQuotedItems(prevItems => {
            const anyChanges = prevItems.some((item, idx) => (item.id || item.name || parseFloat(item.price) > 0 || parseFloat(item.totalItem) > 0) && idx !== selectedQuotedItemIndex && !item.articleLocked);
            if (anyChanges) {
                return prevItems.map((item, idx) => {
                    if ((item.id || item.name || parseFloat(item.price) > 0 || parseFloat(item.totalItem) > 0) && idx !== selectedQuotedItemIndex && !item.articleLocked) {
                        return { ...item, articleLocked: true };
                    }
                    return item;
                });
            }
            return prevItems;
        });
    }, [selectedQuotedItemIndex, quotedItems.length]);

    const activeCompanyId = user?.empresa_activa?.c_nuges || user?.empresa_activa?.id;

    // Efecto para buscar clientes de la Base de Datos reales
    useEffect(() => {
        setIsLoadingClients(true);
        axios.get('http://127.0.0.1:8000/api/clientes/')
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : [];
                setDbClients(data);
                console.log(`[Cotizador] ${data.length} clientes cargados para empresa: ${activeCompanyId}`);
                setIsLoadingClients(false);
            })
            .catch(err => {
                console.error("Error obteniendo clientes:", err);
                setIsLoadingClients(false);
            });
    }, [activeCompanyId]);

    // Efecto para buscar vendedores de la Base de Datos reales
    useEffect(() => {
        axios.get('http://127.0.0.1:8000/api/vendedores/')
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : [];
                setDbSellers(data);
            })
            .catch(err => console.error("Error obteniendo vendedores:", err));
    }, [activeCompanyId]);

    // Efecto para buscar condiciones de pago
    useEffect(() => {
        axios.get('http://127.0.0.1:8000/api/parametros/?c_param=PG')
            .then(res => {
                const arr = Array.isArray(res.data) ? res.data : [];
                const sorted = [...arr].sort((a, b) => parseInt(a.c_secue, 10) - parseInt(b.c_secue, 10));
                setPaymentConditions(sorted);
            })
            .catch(err => console.error("Error obteniendo condiciones de pago:", err));
    }, [activeCompanyId]);

    // Efecto para buscar condiciones de IVA (c_param = IC)
    useEffect(() => {
        axios.get('http://127.0.0.1:8000/api/parametros/?c_param=IC')
            .then(res => {
                setIvaConditions(Array.isArray(res.data) ? res.data : []);
            })
            .catch(err => console.error("Error obteniendo condiciones de IVA:", err));
    }, [activeCompanyId]);

    // Efecto para buscar listas de precios reales
    useEffect(() => {
        setIsLoadingLists(true);
        axios.get('http://127.0.0.1:8000/api/listas/')
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : [];
                setDbLists(data);
                console.log(`[Cotizador] ${data.length} listas de precios cargadas para empresa: ${activeCompanyId}`);
                setIsLoadingLists(false);
            })
            .catch(err => {
                console.error("Error obteniendo listas:", err);
                setIsLoadingLists(false);
            });
    }, [activeCompanyId]);

    // Efecto para sincronizar datos si el usuario tipea el ID antes de que terminen de cargar las listas
    useEffect(() => {
        if (!isLoadingClients && !isLoadingLists && dbClients.length > 0 && client.id && !client.name) {
            const vendorCode = isVendorOnly ? String(user.c_vende).trim() : null;

            const found = dbClients.find(c => {
                const matchesId = parseInt(c.c_clien, 10) === parseInt(client.id, 10);
                if (!matchesId) return false;
                if (vendorCode) {
                    return c.c_vende !== undefined && c.c_vende !== null && String(c.c_vende).trim() === vendorCode;
                }
                return true;
            });

            if (found) {
                setClient(prev => ({
                    ...prev,
                    name: found.d_razon,
                    address: (found.d_domic || '').trim(),
                    d_entre1: (found.d_entre1 || '').trim(),
                    d_entre2: (found.d_entre2 || '').trim(),
                    vatStatus: getIvaLabel(found.c_iva)
                }));
                
                // Re-ejecutar lógica de lista
                let targetList = null;
                const assignedListaNum = parseInt(found.n_lista, 10);
                if (assignedListaNum && assignedListaNum !== 0) {
                    targetList = dbLists.find(l => parseInt(l.n_lista, 10) === assignedListaNum);
                } else {
                    const defaultLists = dbLists.filter(l => l.l_prede);
                    if (defaultLists.length === 1) targetList = defaultLists[0];
                }
                if (targetList) {
                    setList({
                        id: targetList.n_lista,
                        description: targetList.d_obser || `Lista ${targetList.n_lista}`
                    });
                }
                setPaymentCondition(found.c_pago != null ? parseInt(found.c_pago, 10) : '');
            setDeliveryAddressOption('fiscal');
            setManualDeliveryAddress('');
            setDeliveryAddress((found.d_domic || '').trim());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbClients, dbLists, isLoadingClients, isLoadingLists, client.id, user]);

    // Efecto para buscar rubros reales
    useEffect(() => {
        setIsLoadingRubros(true);
        axios.get('http://127.0.0.1:8000/api/rubros/')
            .then(res => {
                const sorted = [...res.data].sort((a, b) => a.d_rubro.trim().localeCompare(b.d_rubro.trim(), 'es', { sensitivity: 'base' }));
                setRubros(sorted);
                setIsLoadingRubros(false);
            })
            .catch(err => {
                console.error("Error obteniendo rubros:", err);
                setIsLoadingRubros(false);
            });
    }, [activeCompanyId]);

    // Efecto para precargar los primeros artículos del sistema para búsqueda rápida
    useEffect(() => {
        setIsGlobalLoading(true);
        setSelectedRubro(null);
        setAvailableArticles([]);
        setQuotedItems([]);
        setClient(initialClientState);
        setList(initialListState);
        setPricesByArtic({});
        axios.get('http://127.0.0.1:8000/api/articulos/?page=1&page_size=100')
            .then(res => {
                const data = res.data;
                setRawGlobalArticles(data.results || (Array.isArray(data) ? data : []));
                setIsGlobalLoading(false);
            })
            .catch(err => {
                console.error('Error fetching global articles', err);
                setIsGlobalLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCompanyId]);

    // Efecto para buscar precios cuando cambia la lista seleccionada
    useEffect(() => {
        if (list.id) {
            axios.get(`http://127.0.0.1:8000/api/precios/?n_lista=${list.id}`)
                .then(res => {
                    const priceMap = {};
                    res.data.forEach(p => {
                        priceMap[p.c_artic] = p.i_conta; // Editado: usar i_conta como pidio el usuariio
                    });
                    setPricesByArtic(priceMap);
                })
                .catch(err => console.error("Error obteniendo precios:", err));
        }
    }, [list.id]);

    const globalArticles = useMemo(() => {
        const listToMap = Array.isArray(rawGlobalArticles)
            ? rawGlobalArticles
            : (rawGlobalArticles && Array.isArray(rawGlobalArticles.results) ? rawGlobalArticles.results : []);
        if (listToMap.length === 0) return [];
        return listToMap.map(art => {
            const listPrice = pricesByArtic[art.c_artic];
            const rubro = rubros.find(r => r.c_rubro === art.c_rubro);
            return {
                id: art.c_artic,
                name: art.d_artic,
                price: listPrice !== undefined && listPrice !== null ? parseFloat(listPrice) : parseFloat(art.i_conta || 0),
                bulto: art.q_unxbu !== undefined && art.q_unxbu !== null ? parseFloat(art.q_unxbu) : 1,
                q_medid: art.q_medid !== undefined && art.q_medid !== null ? parseFloat(art.q_medid) : 1,
                description: art.d_arti2 || art.d_artic,
                notes: "",
                c_iva: art.c_iva,
                c_barra: art.c_barra || "",
                c_situa: art.c_situa,
                c_vendev: (art.c_vendev && art.c_vendev.trim()) ? art.c_vendev : "V",
                rubroId: art.c_rubro,
                rubroName: rubro ? rubro.d_rubro : 'Sin Rubro',
                // Campos extras pedidos para persistencia
                t_artic: art.t_artic,
                i_costo: art.i_costo !== undefined && art.i_costo !== null ? parseFloat(art.i_costo) : 0,
                i_suger: art.i_suger,
                c_moned: art.c_moned,
                c_descli: art.c_descli,
                c_varian: art.c_varian,
                c_ubica: art.c_ubica,
                d_pasil: art.d_pasil,
                d_estan: art.d_estan,
                d_posic: art.d_posic,
                d_imagen: art.d_imagen,
                d_pathimg: art.d_pathimg,
                d_undisky: art.d_undisky,
                c_unmed: art.c_unmed,
                c_ingbr: art.c_ingbr,
                c_impuc: art.c_impuc,
                l_trazar: art.l_trazar,
                q_pesuni: art.q_pesuni
            };
        }).sort((a, b) => a.name.localeCompare(b.name));
    }, [rawGlobalArticles, pricesByArtic, rubros]);

    // Función para abrir el modal global
    const handleOpenGlobalModal = (searchTerm = '', rowIndex = null) => {
        const term = typeof searchTerm === 'string' ? searchTerm : '';
        setGlobalSearchTerm(term);
        if (rowIndex !== null && rowIndex !== undefined) {
            setActiveRowIndex(rowIndex);
            setSelectedQuotedItemIndex(rowIndex);
        } else if (selectedQuotedItemIndex !== null && selectedQuotedItemIndex >= 0) {
            setActiveRowIndex(selectedQuotedItemIndex);
        }
        setShowGlobalArticleModal(true);
            
        if (list.id) {
            axios.get(`http://127.0.0.1:8000/api/precios/?n_lista=${list.id}`)
                .then(res => {
                    const priceMap = {};
                    res.data.forEach(p => {
                        priceMap[p.c_artic] = p.i_conta;
                    });
                    setPricesByArtic(priceMap);
                })
                .catch(err => console.error("Error obteniendo precios:", err));
        }
    };


    // Resuelve el texto de condición de IVA dado el código numérico del cliente
    const getIvaLabel = (c_iva) => {
        if (!c_iva && c_iva !== 0) return '';
        const match = ivaConditions.find(ic => parseInt(ic.c_secue, 10) === parseInt(c_iva, 10));
        return match ? match.d_secue : String(c_iva);
    };

    // --- SISTEMA DE CÁLCULO IMPOSITIVO DILIGENTE ---
    const recalculateRowTaxes = async (index, row, clientId, empresaId) => {
        if (!row.id || row.id === 'LITERAL' || row.t_item === 'LIT') return row;
        
        try {
            const disc = parseFloat(row.discountSurcharge) || 0;
            let c_desrec = ' ';
            let p_desc2 = 0;
            if (disc > 0) {
                c_desrec = 'R';
                p_desc2 = disc;
            } else if (disc < 0) {
                c_desrec = 'D';
                p_desc2 = disc;
            }

            const payload = {
                empresa_id: empresaId || 2,
                cliente_id: clientId,
                c_artic: row.id,
                q_artic: parseFloat(row.quantity) || 1,
                precio_base: row.price_base !== undefined ? parseFloat(row.price_base) : (parseFloat(row.price) || 0),
                p_desc1: 0,
                p_desc2: p_desc2,
                c_desrec: c_desrec,
                p_imint: parseFloat(row.p_imint) || 0,
                p_munic: parseFloat(row.p_munic) || 0,
                p_impub: parseFloat(row.p_impub) || 0,
                i_imfijo: parseFloat(row.i_imfijo) || 0,
                p_comis: parseFloat(row.p_comis) || 0
            };
            
            const res = await axios.post('http://127.0.0.1:8000/api/cotizador/calcular-impuestos/', payload);
            if (res.data) {
                return {
                    ...row,
                    price: parseFloat(res.data.i_unita), 
                    totalItem: parseFloat(res.data.i_item),
                    i_desc2: parseFloat(res.data.i_desc2 || 0),
                    c_desrec: res.data.c_desrec || c_desrec,
                    i_iva: parseFloat(res.data.i_iva || 0),
                    i_iva1: parseFloat(res.data.i_iva1 ?? res.data.i_iva ?? 0),
                    i_iva2: parseFloat(res.data.i_iva2 || 0),
                    i_iva3: parseFloat(res.data.i_iva3 || 0),
                    i_ivaimp: parseFloat(res.data.i_ivaimp || 0),
                    p_ivaart: parseFloat(res.data.p_ivaart || 0),
                    c_caliv: res.data.c_caliv || 'S',
                    i_rg212: parseFloat(res.data.i_rg212),
                    i_imint: parseFloat(res.data.i_imint),
                    i_munic: parseFloat(res.data.i_munic),
                    i_impub: parseFloat(res.data.i_impub),
                    i_ingbr: parseFloat(res.data.i_ingbr),
                    p_ingbr: parseFloat(res.data.p_ingbr),
                    q_tunid: parseInt(res.data.q_tunid, 10),
                    i_comis: parseFloat(res.data.i_comis)
                };
            }
        } catch (err) {
            console.error("Error recalculando impuestos del renglón:", err);
        }
        return row;
    };

    const recalculateAllTaxes = async (clientId, empresaId, itemsList = quotedItems) => {
        if (!clientId) return;
        const updated = await Promise.all(itemsList.map(async (item, idx) => {
            if (item.id && item.id !== 'LITERAL' && item.t_item !== 'LIT') {
                const res = await recalculateRowTaxes(idx, item, clientId, empresaId);
                const calcKey = `${clientId || ''}_${item.id}_${item.quantity}_${item.price_base !== undefined ? item.price_base : item.price}_${item.discountSurcharge}`;
                return { ...res, _lastCalculatedKey: calcKey };
            }
            return item;
        }));
        setQuotedItems(updated);
    };

    const updateAndCalculateRow = async (index, newRow, clientId = client.id, empresaId = user?.empresa_activa?.id, customList = null) => {
        const calcKey = `${clientId || ''}_${newRow.id}_${newRow.quantity}_${newRow.price_base !== undefined ? newRow.price_base : newRow.price}_${newRow.discountSurcharge}`;

        if (!clientId) {
            const basePrice = newRow.price_base !== undefined ? parseFloat(newRow.price_base) : (parseFloat(newRow.price) || 0);
            const qty = parseFloat(newRow.quantity) || 0;
            const disc = parseFloat(newRow.discountSurcharge) || 0;
            const factor = 1 + (disc / 100);
            const finalPrice = basePrice * factor;
            newRow.price = finalPrice;
            newRow.totalItem = finalPrice * qty;
            newRow.i_desc2 = basePrice * (disc / 100) * qty;
            newRow.c_desrec = disc > 0 ? 'R' : (disc < 0 ? 'D' : ' ');
            newRow._lastCalculatedKey = calcKey;
            
            setQuotedItems(prevItems => {
                const listToUse = customList || prevItems;
                if (!customList && index < listToUse.length) {
                    const currentRow = listToUse[index];
                    if (currentRow && (
                        currentRow.quantity !== newRow.quantity ||
                        currentRow.boxes !== newRow.boxes ||
                        currentRow.discountSurcharge !== newRow.discountSurcharge ||
                        currentRow.price_base !== newRow.price_base
                    )) {
                        return listToUse;
                    }
                }
                const updated = [...listToUse];
                while (updated.length <= index) {
                    updated.push({ id: '', name: '', price: 0, quantity: 1, boxes: 0, discountSurcharge: 0, totalItem: 0, notes: '', t_item: 'ART', c_vendev: 'V' });
                }
                updated[index] = newRow;
                return updated;
            });
            return;
        }
        
        const calculated = await recalculateRowTaxes(index, newRow, clientId, empresaId);
        calculated._lastCalculatedKey = calcKey;

        setQuotedItems(prevItems => {
            const listToUse = customList || prevItems;
            if (!customList && index < listToUse.length) {
                const currentRow = listToUse[index];
                if (currentRow) {
                    if (currentRow.id !== newRow.id) return listToUse;
                    if (
                        currentRow.quantity !== newRow.quantity ||
                        currentRow.boxes !== newRow.boxes ||
                        currentRow.discountSurcharge !== newRow.discountSurcharge ||
                        currentRow.price_base !== newRow.price_base
                    ) {
                        return listToUse;
                    }
                }
            }
            const updated = [...listToUse];
            while (updated.length <= index) {
                updated.push({ id: '', name: '', price: 0, quantity: 1, boxes: 0, discountSurcharge: 0, totalItem: 0, notes: '', t_item: 'ART', c_vendev: 'V' });
            }
            const latestNotes = (prevItems[index] && prevItems[index].id === calculated.id && prevItems[index].notes !== undefined)
                ? prevItems[index].notes
                : calculated.notes;
            updated[index] = { ...calculated, notes: latestNotes };
            return updated;
        });
    };

    // --- CONSTANTES ---
    const itemsPerPage = 5; // Cuántas listas de precios mostrar por página en el modal

    // --- FUNCIONES DE ACCIÓN (MANEJAN LOS CLICS Y CAMBIOS) ---

    // Abre el buscador de clientes
    const handleShowClientModal = () => setShowClientModal(true);
    const handleCloseClientModal = () => setShowClientModal(false);

    // Abre el buscador de listas de precios
    const handleShowListModal = () => setShowListModal(true);
    const handleCloseListModal = () => {
        setShowListModal(false);
        setListSearch('');
        setListPage(1);
    };

    // Selecciona un cliente del modal y lo carga en la cabecera
    const handleSelectClient = (selectedClient) => {
        setClient({
            id: selectedClient.c_clien,
            name: selectedClient.d_razon || '',
            address: (selectedClient.d_domic || '').trim(),
            d_entre1: (selectedClient.d_entre1 || '').trim(),
            d_entre2: (selectedClient.d_entre2 || '').trim(),
            vatStatus: getIvaLabel(selectedClient.c_iva)
        });
        
        // Lógica de carga de lista: si es 0, buscar predeterminada
        let targetList = null;
        const assignedListaNum = parseInt(selectedClient.n_lista, 10);

        if (assignedListaNum && assignedListaNum !== 0) {
            targetList = dbLists.find(l => parseInt(l.n_lista, 10) === assignedListaNum);
        } else {
            // El cliente tiene lista 0 o no tiene: buscamos en dbLists las que sean l_prede
            const defaultLists = dbLists.filter(l => l.l_prede);
            if (defaultLists.length === 1) {
                targetList = defaultLists[0];
            } else if (defaultLists.length > 1) {
                // Hay varias predeterminadas: abrimos modal para que elija
                setShowListModal(true);
            }
        }

        if (targetList) {
            setList({
                id: targetList.n_lista,
                description: targetList.d_obser || `Lista ${targetList.n_lista}`
            });
        } else if (!assignedListaNum || assignedListaNum === 0) {
            // Si llegamos acá y no hay predeterminada única, limpiamos descripción
            setList({ id: '0', description: '' });
        }
        
        setPaymentCondition(selectedClient.c_pago != null ? parseInt(selectedClient.c_pago, 10) : ''); 
        setDeliveryAddressOption('fiscal');
        setManualDeliveryAddress('');
        setDeliveryAddress((selectedClient.d_domic || '').trim());
        recalculateAllTaxes(selectedClient.c_clien, user?.empresa_activa?.id);
    };

    const loggedSeller = React.useMemo(() => {
        if (!user?.c_vende || !dbSellers || dbSellers.length === 0) return null;
        return dbSellers.find(s => String(s.c_vende).trim() === String(user.c_vende).trim());
    }, [dbSellers, user]);

    const isVendorOnly = React.useMemo(() => {
        if (!user?.c_vende || user?.l_admin) return false;
        if (loggedSeller) {
            return Boolean(loggedSeller.l_filven);
        }
        return false;
    }, [user, loggedSeller]);

    const handleCloseUnassignedError = () => {
        setUnassignedClientError(false);
        setTimeout(() => {
            const inputEl = document.getElementById('header-client-id');
            if (inputEl) {
                inputEl.focus();
                inputEl.select();
            }
        }, 100);
    };

    const validateClientVendorAccess = (clientId) => {
        if (!clientId || String(clientId).trim() === '') return true;
        const vendorCode = isVendorOnly ? String(user.c_vende).trim() : null;

        if (vendorCode) {
            const foundInDb = dbClients.find(c => parseInt(c.c_clien, 10) === parseInt(clientId, 10));
            if (foundInDb && String(foundInDb.c_vende).trim() !== vendorCode) {
                setUnassignedClientError(true);
                setClient(initialClientState);
                return false;
            }
        }
        return true;
    };

    const handleClientChange = (e) => {
        const newId = e.target.value;
        const vendorCode = isVendorOnly ? String(user.c_vende).trim() : null;

        // Búsqueda flexible: solo vinculamos los datos del cliente si existe y pertenece al vendedor
        const found = dbClients.find(c => {
            const matchesId = newId !== '' && parseInt(c.c_clien, 10) === parseInt(newId, 10);
            if (!matchesId) return false;
            if (vendorCode) {
                return c.c_vende !== undefined && c.c_vende !== null && String(c.c_vende).trim() === vendorCode;
            }
            return true;
        });

        setClient({
            id: newId,
            name: found ? found.d_razon : '',
            address: found ? (found.d_domic || '').trim() : '',
            d_entre1: found ? (found.d_entre1 || '').trim() : '',
            d_entre2: found ? (found.d_entre2 || '').trim() : '',
            vatStatus: found ? getIvaLabel(found.c_iva) : ''
        });
        if (found) {
            // Lógica de carga de lista para tipeo manual
            let targetList = null;
            const assignedListaNum = parseInt(found.n_lista, 10);

            if (assignedListaNum && assignedListaNum !== 0) {
                targetList = dbLists.find(l => parseInt(l.n_lista, 10) === assignedListaNum);
            } else {
                const defaultLists = dbLists.filter(l => l.l_prede);
                if (defaultLists.length === 1) {
                    targetList = defaultLists[0];
                } else if (defaultLists.length > 1) {
                    setShowListModal(true);
                }
            }

            if (targetList) {
                setList({
                    id: targetList.n_lista,
                    description: targetList.d_obser || `Lista ${targetList.n_lista}`
                });
            } else {
                setList({ id: assignedListaNum || '0', description: '' });
            }
            setPaymentCondition(found.c_pago != null ? parseInt(found.c_pago, 10) : '');
            setDeliveryAddressOption('fiscal');
            setManualDeliveryAddress('');
            setDeliveryAddress((found.d_domic || '').trim());
            recalculateAllTaxes(found.c_clien, user?.empresa_activa?.id);
        }
    };

    // Se dispara cuando escribís el N° de Lista a mano
    const handleListChange = (e) => {
        const newId = e.target.value;
        const found = dbLists.find(l => String(l.n_lista) === String(newId));
        setList({
            id: newId,
            description: found ? (found.d_obser || `Lista ${found.n_lista}`) : ''
        });
        setSelectedRubro(null);
        setAvailableArticles([]);
    };

    // Formatea una cantidad a formato numérico (1.234,56)
    const formatQuantity = (amount) => {
        const num = typeof amount === 'number' ? amount : parseFloat(String(amount || 0).replace(',', '.'));
        return new Intl.NumberFormat('es-AR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(isNaN(num) ? 0 : num);
    };

    // Formatea un número a pesos argentinos (1.234,560)
    const formatCurrency = (amount) => {
        const num = typeof amount === 'number' ? amount : parseFloat(String(amount || 0).replace(',', '.'));
        return new Intl.NumberFormat('es-AR', {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
        }).format(isNaN(num) ? 0 : num);
    };

    // BOTÓN CANCELAR: Resetea TODO el cotizador a cero
    const handleReset = () => {
        setShowSaveDraftModal(false);
        setShowFinalConfirmModal(false);
        setEditingDraftId(null);
        setEditingDraftLabel('');
        loadedDraftKeyRef.current = null;
        if (location.search || location.state?.draftQuote) {
            navigate('/quoter', { replace: true, state: {} });
        }
        setIsHeaderConfirmed(false);
        setIsHeaderExpanded(true);
        setIsClientLocked(false);
        setClient(initialClientState);
        setList(initialListState);
        setPaymentCondition('');
        setQuotedItems([]);
        setSelectedQuotedItemIndex(null);
        setSelectedRubro(null);
        setAvailableArticles([]);
        setGeneralNote('');
        setDeliveryAddress('');
        setDeliveryAddressOption('fiscal');
        setManualDeliveryAddress('');
        setShowDeliveryAddressModal(false);
        setShowCancelModal(false);
    };

    const handleSaveBudget = async () => {
        if (!client.id) {
            Swal.fire({
                title: 'Atención',
                text: 'Debe seleccionar un cliente antes de guardar.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar'
            });
            return;
        }
        if (quotedItems.length === 0 || !quotedItems.some(item => item.id && item.name)) {
            Swal.fire({
                title: 'Atención',
                text: 'Debe agregar al menos un artículo al presupuesto.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        setIsSaving(true);
        try {
            // Obtener datos del cliente del listado de la DB
            const selectedClient = dbClients.find(c => String(c.c_clien) === String(client.id));
            if (!selectedClient) {
                throw new Error("No se encontró el cliente seleccionado en la base de datos.");
            }

            // Armar cabecera (header)
            const header = {
                c_compr: 'PRE',
                t_presu: selectedClient.t_presu || 'A',
                c_clien: selectedClient.c_clien,
                c_flete: selectedClient.c_flete || 0,
                n_sucli: selectedClient.n_sucli || 0,
                i_grava: subtotalNeto,
                i_graimp: totalIVA,
                i_total: finalGrandTotal,
                i_iva1: totalIVA1,
                i_iva2: totalIVA2,
                i_iva3: totalIVA3,
                i_ivaimp: totalIVAimp,
                i_ingbru: totalIIBB,
                c_pago: paymentCondition,
                n_lista: list.id,
                c_vende: selectedClient.c_vende || 0,
                p_comis: parseFloat(selectedClient.p_comis) || 0,
                d_obser: generalNote || '',
                d_entre: (deliveryAddress || selectedClient.d_domic || '').trim().substring(0, 50),
                c_entre_opt: deliveryAddressOption || 'fiscal',
                d_entre_manual: deliveryAddressOption === 'manual' ? (manualDeliveryAddress || '').trim().substring(0, 50) : '',
            };

            // Armar ítems (items)
            const items = quotedItems
                .filter(item => item.id && item.name)
                .map(item => {
                    const priceVal = parseFloat(item.price) || 0;
                    const priceBaseVal = parseFloat(item.price_base) || priceVal;
                    const qtyVal = parseFloat(item.quantity) || 1;
                    const itemIvaimp = parseFloat(item.i_ivaimp) || 0;
                    const itemIva1 = parseFloat(item.i_iva1 !== undefined ? item.i_iva1 : (itemIvaimp > 0 ? 0 : item.i_iva)) || 0;
                    const itemIva2 = parseFloat(item.i_iva2) || 0;
                    const itemIva3 = parseFloat(item.i_iva3) || 0;

                    const discVal = parseFloat(item.discountSurcharge !== undefined && item.discountSurcharge !== null && item.discountSurcharge !== '' ? item.discountSurcharge : (item.p_desc2 || item.p_descu || 0)) || 0;

                    let c_desrec = item.c_desrec || ' ';
                    let p_desc2 = discVal;
                    if (discVal > 0) {
                        c_desrec = 'R';
                        p_desc2 = Math.abs(discVal);
                    } else if (discVal < 0) {
                        c_desrec = 'D';
                        p_desc2 = -Math.abs(discVal);
                    } else if (c_desrec === 'D') {
                        p_desc2 = -Math.abs(p_desc2);
                    } else if (c_desrec === 'R') {
                        p_desc2 = Math.abs(p_desc2);
                    }

                    let i_desc2 = (item.i_desc2 !== undefined && item.i_desc2 !== null)
                        ? parseFloat(item.i_desc2)
                        : (priceBaseVal * (discVal / 100) * qtyVal);

                    if (discVal < 0 || c_desrec === 'D') {
                        p_desc2 = -Math.abs(p_desc2);
                        i_desc2 = -Math.abs(i_desc2);
                    } else if (discVal > 0 || c_desrec === 'R') {
                        p_desc2 = Math.abs(p_desc2);
                        i_desc2 = Math.abs(i_desc2);
                    }

                    return {
                        c_artic: item.id,
                        q_artic: qtyVal,
                        i_costo: parseFloat(item.i_costo) || 0,
                        i_unita: priceVal,
                        i_lista: priceBaseVal,
                        i_publi: priceBaseVal,
                        i_campre: priceBaseVal,
                        p_desc2: p_desc2,
                        i_desc2: i_desc2,
                        c_desrec: c_desrec,
                        discountSurcharge: discVal,
                        i_venta: parseFloat(item.totalItem) || (priceVal * qtyVal),
                        c_caliv: item.c_caliv || 'S',
                        p_ivaart: parseFloat(item.p_ivaart) || 0,
                        i_iva1: itemIva1,
                        i_iva2: itemIva2,
                        i_iva3: itemIva3,
                        i_ivaimp: itemIvaimp,
                        i_iva: itemIva1 + itemIva2 + itemIva3 + itemIvaimp,
                        t_artic: item.t_item === 'LIT' ? 1 : 1,
                        q_bulto: parseFloat(item.boxes) || 0,
                        d_artic: item.name || '',
                        d_obser: item.notes || '',
                        t_item: item.t_item || 'ART',
                        // Campos de estado del frontend para restaurar borradores sin pérdida
                        id: item.id,
                        name: item.name || '',
                        price: priceVal,
                        price_base: priceBaseVal,
                        quantity: qtyVal,
                        boxes: parseFloat(item.boxes) || 0,
                        bulto: parseFloat(item.bulto) || 1,
                        q_medid: parseFloat(item.q_medid) || 1,
                        c_unmed: item.c_unmed,
                        q_pesuni: parseFloat(item.q_pesuni) || 0,
                        totalItem: parseFloat(item.totalItem) || (priceVal * qtyVal),
                        notes: item.notes || '',
                        c_iva: item.c_iva,
                        c_vendev: item.c_vendev || 'V',
                        i_ingbr: parseFloat(item.i_ingbr) || 0,
                        i_munic: parseFloat(item.i_munic) || 0,
                        i_impub: parseFloat(item.i_impub) || 0,
                        i_imint: parseFloat(item.i_imint) || 0,
                        i_rg212: parseFloat(item.i_rg212) || 0,
                    };
                });

            const payload = {
                header,
                items,
                ordenar_por_codigo: false,
                borrador_id: editingDraftId || null
            };

            const response = await axios.post('http://127.0.0.1:8000/api/presupuestos/', payload, {
                params: { company: activeCompanyId },
                headers: { 'X-Company-ID': String(activeCompanyId || '') }
            });
            
            if (response.status === 201) {
                setShowConfirmModal(false);
                setShowConfirmModal(false);
                setShowFinalConfirmModal(false);
                setEditingDraftId(null);
                setEditingDraftLabel('');
                loadedDraftKeyRef.current = null;
                if (location.search || location.state?.draftQuote) {
                    navigate('/quoter', { replace: true, state: {} });
                }
                setSavedQuoteData(response.data);
                setShowSuccessModal(true);
            } else {
                Swal.fire({
                    title: 'Error',
                    text: 'Error al guardar el presupuesto.',
                    icon: 'error',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Aceptar'
                });
            }
        } catch (error) {
            console.error("Error guardando presupuesto:", error);
            const errorMsg = error.response?.data?.error || error.message || "Error desconocido";
            Swal.fire({
                title: 'Error',
                text: `Error al guardar el presupuesto: ${errorMsg}`,
                icon: 'error',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Guardar presupuesto como BORRADOR (tabla mpre_borrador)
    const handleSaveDraft = async () => {
        if (!client.id) {
            Swal.fire({
                title: 'Atención',
                text: 'Debe seleccionar un cliente antes de guardar el borrador.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar'
            });
            return;
        }
        if (quotedItems.length === 0 || !quotedItems.some(item => item.id && item.name)) {
            Swal.fire({
                title: 'Atención',
                text: 'Debe agregar al menos un artículo para guardar el borrador.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        setIsSaving(true);
        try {
            const selectedClient = dbClients.find(c => String(c.c_clien) === String(client.id));
            if (!selectedClient) {
                throw new Error("No se encontró el cliente seleccionado en la base de datos.");
            }

            const header = {
                c_compr: 'BOR',
                t_presu: selectedClient.t_presu || 'A',
                c_clien: selectedClient.c_clien,
                c_flete: selectedClient.c_flete || 0,
                n_sucli: selectedClient.n_sucli || 0,
                i_grava: subtotalNeto,
                i_graimp: totalIVA,
                i_total: finalGrandTotal,
                i_iva1: totalIVA1,
                i_iva2: totalIVA2,
                i_iva3: totalIVA3,
                i_ivaimp: totalIVAimp,
                i_ingbru: totalIIBB,
                c_pago: paymentCondition,
                n_lista: list.id,
                c_vende: selectedClient.c_vende || 0,
                p_comis: parseFloat(selectedClient.p_comis) || 0,
                d_obser: generalNote || '',
                d_entre: (deliveryAddress || selectedClient.d_domic || '').trim().substring(0, 50),
                c_entre_opt: deliveryAddressOption || 'fiscal',
                d_entre_manual: deliveryAddressOption === 'manual' ? (manualDeliveryAddress || '').trim().substring(0, 50) : '',
            };

            const items = quotedItems
                .filter(item => item.id && item.name)
                .map(item => {
                    const priceVal = parseFloat(item.price) || 0;
                    const priceBaseVal = parseFloat(item.price_base) || priceVal;
                    const qtyVal = parseFloat(item.quantity) || 1;
                    const itemIvaimp = parseFloat(item.i_ivaimp) || 0;
                    const itemIva1 = parseFloat(item.i_iva1 !== undefined ? item.i_iva1 : (itemIvaimp > 0 ? 0 : item.i_iva)) || 0;
                    const itemIva2 = parseFloat(item.i_iva2) || 0;
                    const itemIva3 = parseFloat(item.i_iva3) || 0;
                    const discVal = parseFloat(item.discountSurcharge !== undefined && item.discountSurcharge !== null && item.discountSurcharge !== '' ? item.discountSurcharge : (item.p_desc2 || item.p_descu || 0)) || 0;

                    let c_desrec = item.c_desrec || ' ';
                    let p_desc2 = discVal;
                    if (discVal > 0) {
                        c_desrec = 'R';
                        p_desc2 = Math.abs(discVal);
                    } else if (discVal < 0) {
                        c_desrec = 'D';
                        p_desc2 = -Math.abs(discVal);
                    } else if (c_desrec === 'D') {
                        p_desc2 = -Math.abs(p_desc2);
                    } else if (c_desrec === 'R') {
                        p_desc2 = Math.abs(p_desc2);
                    }

                    let i_desc2 = (item.i_desc2 !== undefined && item.i_desc2 !== null)
                        ? parseFloat(item.i_desc2)
                        : (priceBaseVal * (discVal / 100) * qtyVal);

                    if (discVal < 0 || c_desrec === 'D') {
                        p_desc2 = -Math.abs(p_desc2);
                        i_desc2 = -Math.abs(i_desc2);
                    } else if (discVal > 0 || c_desrec === 'R') {
                        p_desc2 = Math.abs(p_desc2);
                        i_desc2 = Math.abs(i_desc2);
                    }

                    return {
                        c_artic: item.id,
                        q_artic: qtyVal,
                        i_costo: parseFloat(item.i_costo) || 0,
                        i_unita: priceVal,
                        i_lista: priceBaseVal,
                        i_publi: priceBaseVal,
                        i_campre: priceBaseVal,
                        p_desc2: p_desc2,
                        i_desc2: i_desc2,
                        c_desrec: c_desrec,
                        discountSurcharge: discVal,
                        i_venta: parseFloat(item.totalItem) || (priceVal * qtyVal),
                        c_caliv: item.c_caliv || 'S',
                        p_ivaart: parseFloat(item.p_ivaart) || 0,
                        i_iva1: itemIva1,
                        i_iva2: itemIva2,
                        i_iva3: itemIva3,
                        i_ivaimp: itemIvaimp,
                        i_iva: itemIva1 + itemIva2 + itemIva3 + itemIvaimp,
                        t_artic: 1,
                        q_bulto: parseFloat(item.boxes) || 0,
                        d_artic: item.name || '',
                        d_obser: item.notes || '',
                        t_item: item.t_item || 'ART',
                        id: item.id,
                        name: item.name || '',
                        price: priceVal,
                        price_base: priceBaseVal,
                        quantity: qtyVal,
                        boxes: parseFloat(item.boxes) || 0,
                        bulto: parseFloat(item.bulto) || 1,
                        q_medid: parseFloat(item.q_medid) || 1,
                        c_unmed: item.c_unmed,
                        q_pesuni: parseFloat(item.q_pesuni) || 0,
                        totalItem: parseFloat(item.totalItem) || (priceVal * qtyVal),
                        notes: item.notes || '',
                        c_iva: item.c_iva,
                        c_vendev: item.c_vendev || 'V',
                        i_ingbr: parseFloat(item.i_ingbr) || 0,
                        i_munic: parseFloat(item.i_munic) || 0,
                        i_impub: parseFloat(item.i_impub) || 0,
                        i_imint: parseFloat(item.i_imint) || 0,
                        i_rg212: parseFloat(item.i_rg212) || 0,
                    };
                });

            const payload = {
                header,
                items,
                borrador_id: editingDraftId || null
            };

            const response = await axios.post('http://127.0.0.1:8000/api/presupuestos/borradores/', payload, {
                params: { company: activeCompanyId },
                headers: { 'X-Company-ID': String(activeCompanyId || '') }
            });
            if (response.status === 201 || response.status === 200) {
                const numeroBorrador = response.data?.numero_formateado || `BOR ${String(response.data?.borrador_id || '').padStart(8, '0')}`;
                setShowSaveDraftModal(false);
                setShowConfirmModal(false);
                handleReset();
                Swal.fire({
                    title: 'Presupuesto Guardado',
                    text: `El presupuesto se guardó correctamente como borrador (${numeroBorrador}). Diríjase a la Consulta de Presupuestos para seguir editando.`,
                    icon: 'success',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Aceptar'
                });
            }
        } catch (error) {
            console.error("Error guardando borrador:", error);
            const errorMsg = error.response?.data?.error || error.message || "Error desconocido";
            Swal.fire({
                title: 'Error',
                text: `Error al guardar el borrador: ${errorMsg}`,
                icon: 'error',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Efecto para cargar un presupuesto borrador cuando se redirige desde Consulta de Presupuestos
    useEffect(() => {
        if (isLoadingClients || isLoadingLists || dbClients.length === 0 || dbLists.length === 0) return;

        const searchParams = new URLSearchParams(location.search);
        const queryBorradorId = searchParams.get('borrador');
        const stateDraft = location.state?.draftQuote;
        const targetId = stateDraft?.borrador_id || stateDraft?.id || stateDraft?.n_presu || queryBorradorId;

        if (!targetId) return;
        const draftKey = `${activeCompanyId}-${targetId}`;
        if (loadedDraftKeyRef.current === draftKey) return;

        const applyDraftToQuoter = (draftObj) => {
            if (!draftObj) return;
            loadedDraftKeyRef.current = draftKey;
            const bId = draftObj.borrador_id || draftObj.id || draftObj.n_presu;
            setEditingDraftId(bId);
            setEditingDraftLabel(`BOR ${String(bId).padStart(8, '0')}`);

            const foundClient = dbClients.find(c => parseInt(c.c_clien, 10) === parseInt(draftObj.c_clien, 10));
            const cDomic = (foundClient?.d_domic || draftObj.d_domic || draftObj.cliente_domicilio || '').trim();
            const cEntre1 = (foundClient?.d_entre1 || draftObj.d_entre1 || '').trim();
            const cEntre2 = (foundClient?.d_entre2 || draftObj.d_entre2 || '').trim();

            if (foundClient) {
                setClient({
                    id: foundClient.c_clien,
                    name: foundClient.d_razon || '',
                    address: cDomic,
                    d_entre1: cEntre1,
                    d_entre2: cEntre2,
                    vatStatus: getIvaLabel(foundClient.c_iva)
                });
            } else {
                setClient({
                    id: draftObj.c_clien || '',
                    name: draftObj.cliente_nombre || draftObj.d_razon || '',
                    address: cDomic,
                    d_entre1: cEntre1,
                    d_entre2: cEntre2,
                    vatStatus: draftObj.cliente_c_iva ? getIvaLabel(draftObj.cliente_c_iva) : ''
                });
            }

            const foundList = dbLists.find(l => parseInt(l.n_lista, 10) === parseInt(draftObj.n_lista, 10));
            if (foundList) {
                setList({
                    id: foundList.n_lista,
                    description: foundList.d_obser || `Lista ${foundList.n_lista}`
                });
            } else if (draftObj.n_lista != null) {
                setList({
                    id: draftObj.n_lista,
                    description: `Lista ${draftObj.n_lista}`
                });
            }

            setPaymentCondition(draftObj.c_pago != null ? parseInt(draftObj.c_pago, 10) : '');
            setGeneralNote(draftObj.d_obser || '');

            const isAddressMatch = (savedAddr, clientAddr) => {
                if (!savedAddr || !clientAddr) return false;
                const s = savedAddr.trim().toUpperCase();
                const c = clientAddr.trim().toUpperCase();
                if (s === c) return true;
                if (c.substring(0, 50) === s.substring(0, 50)) return true;
                if (c.startsWith(s) || s.startsWith(c)) return true;
                return false;
            };

            const savedOpt = draftObj.header_json?.c_entre_opt || draftObj.c_entre_opt;
            const draftEntre = (draftObj.d_entre || draftObj.header_json?.d_entre || '').trim();

            if (savedOpt === 'fiscal' || (!savedOpt && (!draftEntre || isAddressMatch(draftEntre, cDomic)))) {
                setDeliveryAddressOption('fiscal');
                setManualDeliveryAddress('');
                setDeliveryAddress(cDomic);
            } else if (savedOpt === 'alt1' || (!savedOpt && cEntre1 && isAddressMatch(draftEntre, cEntre1))) {
                setDeliveryAddressOption('alt1');
                setManualDeliveryAddress('');
                setDeliveryAddress(cEntre1);
            } else if (savedOpt === 'alt2' || (!savedOpt && cEntre2 && isAddressMatch(draftEntre, cEntre2))) {
                setDeliveryAddressOption('alt2');
                setManualDeliveryAddress('');
                setDeliveryAddress(cEntre2);
            } else {
                setDeliveryAddressOption('manual');
                const manualVal = (draftObj.header_json?.d_entre_manual || draftEntre || '').trim();
                setManualDeliveryAddress(manualVal);
                setDeliveryAddress(manualVal);
            }
            const rawItems = Array.isArray(draftObj.items) ? draftObj.items : [];
            const restoredItems = rawItems.map(it => {
                const qty = parseFloat(it.quantity ?? it.q_artic ?? 1) || 1;
                const priceVal = parseFloat(it.price ?? it.i_unita ?? 0) || 0;
                const priceBaseVal = parseFloat(it.price_base ?? it.i_lista ?? priceVal) || priceVal;
                const discVal = parseFloat(it.discountSurcharge ?? it.p_desc2 ?? 0) || 0;
                return {
                    id: String(it.id || it.c_artic || '').trim(),
                    name: it.name || it.d_artic || '',
                    price: priceVal,
                    price_base: priceBaseVal,
                    quantity: qty,
                    boxes: parseFloat(it.boxes ?? it.q_bulto ?? 0) || 0,
                    bulto: parseFloat(it.bulto ?? 1) || 1,
                    q_medid: parseFloat(it.q_medid ?? 1) || 1,
                    c_unmed: it.c_unmed,
                    q_pesuni: parseFloat(it.q_pesuni ?? 0) || 0,
                    discountSurcharge: discVal,
                    totalItem: parseFloat(it.totalItem ?? it.i_venta ?? (priceVal * qty)) || 0,
                    notes: it.notes ?? it.d_obser ?? '',
                    t_item: it.t_item || 'ART',
                    c_vendev: it.c_vendev || 'V',
                    c_iva: it.c_iva,
                    i_costo: parseFloat(it.i_costo ?? 0) || 0,
                    p_ivaart: parseFloat(it.p_ivaart ?? 0) || 0,
                    i_iva: parseFloat(it.i_iva ?? it.i_iva1 ?? 0) || 0,
                    i_iva1: parseFloat(it.i_iva1 ?? it.i_iva ?? 0) || 0,
                    i_iva2: parseFloat(it.i_iva2 ?? 0) || 0,
                    i_iva3: parseFloat(it.i_iva3 ?? 0) || 0,
                    i_ivaimp: parseFloat(it.i_ivaimp ?? 0) || 0,
                    i_ingbr: parseFloat(it.i_ingbr ?? 0) || 0,
                    i_munic: parseFloat(it.i_munic ?? 0) || 0,
                    i_impub: parseFloat(it.i_impub ?? 0) || 0,
                    i_imint: parseFloat(it.i_imint ?? 0) || 0,
                    i_rg212: parseFloat(it.i_rg212 ?? 0) || 0,
                    articleLocked: true
                };
            });

            restoredItems.push({ id: '', name: '', price: 0, quantity: 1, boxes: 0, discountSurcharge: 0, totalItem: 0, notes: '', t_item: 'ART', c_vendev: 'V' });
            setQuotedItems(restoredItems);
            setSelectedQuotedItemIndex(restoredItems.length - 1);
            setIsHeaderConfirmed(true);
            setIsClientLocked(true);
        };

        if (stateDraft && Array.isArray(stateDraft.items)) {
            applyDraftToQuoter(stateDraft);
        } else {
            axios.get(`http://127.0.0.1:8000/api/presupuestos/borradores/${targetId}/`)
                .then(res => applyDraftToQuoter(res.data))
                .catch(err => console.error("Error cargando borrador:", err));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingClients, isLoadingLists, dbClients, dbLists, location.search, location.state, activeCompanyId]);

    // Selecciona una lista del modal
    const handleSelectList = (selectedList) => {
        setList({
            id: selectedList.n_lista,
            description: selectedList.d_obser || `Lista ${selectedList.n_lista}`
        });
        setShowListModal(false);
        setListSearch('');
        setListPage(1);
        setSelectedRubro(null);
        setAvailableArticles([]);
    };

    // BOTONES + y -: Ajustan la cantidad del artículo seleccionado en la tabla
    const handleAdjustQuantity = (amount) => {
        if (selectedQuotedItemIndex === null) return;
        const updated = [...quotedItems];
        const item = updated[selectedQuotedItemIndex];
        const currentQty = parseFloat(item.quantity) || 0;
        
        if (currentQty + amount > 0) {
            item.quantity = currentQty + amount;
            const price = parseFloat(item.price) || 0;
            const disc = parseFloat(item.discountSurcharge) || 0;
            item.totalItem = (price * item.quantity) * (1 + (disc / 100));
            setQuotedItems(updated);
        } else {
            // Si la cantidad llega a 0, borramos el ítem de la tabla
            handleRemoveItem(selectedQuotedItemIndex);
        }
    };

    // Borra un ítem de la tabla o lo limpia si es el único
    const handleRemoveItem = (index) => {
        // Limpiar refs de idValueRef.current para que coincidan con los nuevos índices
        const newIdRefs = {};
        let newIdx = 0;
        quotedItems.forEach((item, i) => {
            if (i !== index) {
                newIdRefs[newIdx] = idValueRef.current[i] || item.id;
                newIdx++;
            }
        });
        idValueRef.current = newIdRefs;

        let updated = quotedItems.filter((_, i) => i !== index);
        if (updated.length === 0) {
            // Si es la única línea, limpiamos su contenido pero no la eliminamos de la lista
            updated = [{ id: '', name: '', price: 0, quantity: 1, boxes: 0, discountSurcharge: 0, totalItem: 0, notes: '', t_item: 'ART', c_vendev: 'V' }];
            setSelectedQuotedItemIndex(0);
        } else {
            setSelectedQuotedItemIndex(null);
        }
        setQuotedItems(updated);
    };

    const requestDeleteRow = (index) => {
        const item = quotedItems[index];
        // Si el renglón ya está totalmente vacío, lo borramos/limpiamos de inmediato sin confirmación
        if (!item.id && !item.name && !item.price && !item.notes) {
            handleRemoveItem(index);
            return;
        }
        setRowToDeleteIndex(index);
        setShowDeleteRowModal(true);
    };

    const confirmDeleteRow = () => {
        if (rowToDeleteIndex !== null) {
            handleRemoveItem(rowToDeleteIndex);
        }
        setShowDeleteRowModal(false);
        setRowToDeleteIndex(null);
    };
    const fetchArticlesByRubro = async (rubroId, pageNumber = 1, isInitial = false) => {
        if (isInitial) {
            setIsLoadingArticles(true);
        } else {
            setIsLoadingMoreRubroArticles(true);
        }

        try {
            const fetchArticulos = axios.get(`http://127.0.0.1:8000/api/articulos/?rubro=${rubroId}&page=${pageNumber}&page_size=100`);
            const fetchPrecios = (list.id && isInitial) ? axios.get(`http://127.0.0.1:8000/api/precios/?n_lista=${list.id}`) : Promise.resolve({ data: [] });

            const [resArticulos, resPrecios] = await Promise.all([fetchArticulos, fetchPrecios]);

            let currentPriceMap = { ...pricesByArtic };
            if (list.id && isInitial && resPrecios.data) {
                const priceMap = {};
                resPrecios.data.forEach(p => {
                    priceMap[p.c_artic] = p.i_conta;
                });
                setPricesByArtic(priceMap);
                currentPriceMap = priceMap;
            }

            const data = resArticulos.data;
            const rawResults = data.results || (Array.isArray(data) ? data : []);
            const hasNext = Boolean(data.next) || rawResults.length === 100;

            const mergedArticles = rawResults.map(art => {
                const listPrice = list.id ? currentPriceMap[art.c_artic] : undefined;
                return {
                    id: art.c_artic,
                    name: art.d_artic,
                    price: listPrice !== undefined && listPrice !== null ? parseFloat(listPrice) : parseFloat(art.i_conta || 0),
                    bulto: art.q_unxbu !== undefined && art.q_unxbu !== null ? parseFloat(art.q_unxbu) : 1,
                    q_medid: art.q_medid !== undefined && art.q_medid !== null ? parseFloat(art.q_medid) : 1,
                    description: art.d_arti2 || art.d_artic,
                    notes: "",
                    c_iva: art.c_iva,
                    t_artic: art.t_artic,
                    i_costo: art.i_costo !== undefined && art.i_costo !== null ? parseFloat(art.i_costo) : 0,
                    i_suger: art.i_suger,
                    c_moned: art.c_moned,
                    c_descli: art.c_descli,
                    c_varian: art.c_varian,
                    c_ubica: art.c_ubica,
                    d_pasil: art.d_pasil,
                    d_estan: art.d_estan,
                    d_posic: art.d_posic,
                    d_imagen: art.d_imagen,
                    d_pathimg: art.d_pathimg,
                    d_undisky: art.d_undisky,
                    c_unmed: art.c_unmed,
                    c_ingbr: art.c_ingbr,
                    c_impuc: art.c_impuc,
                    c_vendev: (art.c_vendev && art.c_vendev.trim()) ? art.c_vendev : "V",
                    l_trazar: art.l_trazar,
                    q_pesuni: art.q_pesuni,
                    c_situa: art.c_situa,
                    c_barra: art.c_barra || ""
                };
            });

            if (isInitial) {
                setAvailableArticles(mergedArticles);
            } else {
                setAvailableArticles(prev => [...prev, ...mergedArticles]);
            }

            setRubroArticlesPage(pageNumber);
            setHasMoreRubroArticles(hasNext);
        } catch (err) {
            console.error("Error obteniendo artículos o precios:", err);
            if (isInitial) setAvailableArticles([]);
            setHasMoreRubroArticles(false);
        } finally {
            setIsLoadingArticles(false);
            setIsLoadingMoreRubroArticles(false);
        }
    };

    const handleSelectRubro = (rubro) => {
        setSelectedRubro(rubro);
        setAvailableArticles([]);
        setRubroArticlesPage(1);
        setHasMoreRubroArticles(true);
        fetchArticlesByRubro(rubro.c_rubro, 1, true);
    };

    const handleRubroArticlesScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 80) {
            if (selectedRubro && hasMoreRubroArticles && !isLoadingArticles && !isLoadingMoreRubroArticles) {
                fetchArticlesByRubro(selectedRubro.c_rubro, rubroArticlesPage + 1, false);
            }
        }
    };

    // AGREGA UN ARTÍCULO A LA TABLA desde uno de los buscadores
    const handleAddArticle = async (article) => {
        let targetIndex = -1;
        const allowSumPro = user?.empresa_activa?.l_sumapro !== false;
        
        // 1. Determinar cuál es la fila de destino (targetIndex)
        let rowToReplace = -1;
        if (activeRowIndex !== null && activeRowIndex >= 0 && activeRowIndex < quotedItems.length) {
            if (!quotedItems[activeRowIndex].name) {
                rowToReplace = activeRowIndex;
            }
        } else if (selectedQuotedItemIndex !== null && selectedQuotedItemIndex >= 0 && selectedQuotedItemIndex < quotedItems.length) {
            // Si la fila seleccionada no tiene artículo confirmado, la usamos
            if (!quotedItems[selectedQuotedItemIndex].name) {
                rowToReplace = selectedQuotedItemIndex;
            }
        }
        
        // Si aún no hay fila de reemplazo vacía, buscar si la última fila está sin confirmar/vacía
        if (rowToReplace === -1) {
            const lastIndex = quotedItems.length - 1;
            if (lastIndex >= 0 && !quotedItems[lastIndex].name) {
                rowToReplace = lastIndex;
            }
        }

        // 2. ¿Ya está en la tabla el artículo? (solo si l_sumapro está activo)
        const existingIndex = allowSumPro ? quotedItems.findIndex(item => String(item.id).trim().toLowerCase() === String(article.id).trim().toLowerCase()) : -1;

        if (existingIndex !== -1) {
            // SÍ: Le sumamos 1 a la cantidad en existingIndex
            const updated = [...quotedItems];
            updated[existingIndex].quantity = (parseFloat(updated[existingIndex].quantity) || 0) + 1;
            
            // Si estábamos buscando desde una fila que no tenía artículo confirmado y no es la existingIndex, la limpiamos
            if (rowToReplace !== -1 && rowToReplace !== existingIndex) {
                if (!updated[rowToReplace].name) {
                    updated[rowToReplace] = { id: '', name: '', price: 0, quantity: 1, boxes: 0, discountSurcharge: 0, totalItem: 0, notes: '', t_item: 'ART' };
                }
            }

            setSelectedQuotedItemIndex(existingIndex);
            targetIndex = existingIndex;
            await updateAndCalculateRow(existingIndex, updated[existingIndex], client.id, user?.empresa_activa?.id, updated);
        } else {
            const articleNote = (article.notes !== undefined && article.notes !== null && article.notes !== '') 
                ? article.notes 
                : '';

            // NO: Si tenemos una fila para reemplazar, la pisamos
            if (rowToReplace !== -1) {
                const currentQuantity = parseFloat(quotedItems[rowToReplace].quantity) || 1;
                const updatedRow = { 
                    ...article, 
                    quantity: currentQuantity, 
                    boxes: parseFloat(quotedItems[rowToReplace].boxes) || 0, 
                    discountSurcharge: parseFloat(quotedItems[rowToReplace].discountSurcharge) || 0, 
                    totalItem: article.price * currentQuantity, 
                    price_base: article.price,
                    notes: (quotedItems[rowToReplace].name ? quotedItems[rowToReplace].notes : articleNote) || articleNote, 
                    t_item: 'ART',
                    articleLocked: true
                };
                if (article.c_unmed === "1" && parseFloat(article.q_pesuni) > 0) {
                    updatedRow.wq_pesuni = currentQuantity * parseFloat(article.q_pesuni);
                }
                setSelectedQuotedItemIndex(rowToReplace);
                targetIndex = rowToReplace;
                const updatedList = [...quotedItems];
                updatedList[rowToReplace] = updatedRow;
                await updateAndCalculateRow(rowToReplace, updatedRow, client.id, user?.empresa_activa?.id, updatedList);
            } else {
                // SINO: Agregamos una nueva fila al final
                const newItem = { 
                    ...article, 
                    quantity: 1, 
                    boxes: 0, 
                    discountSurcharge: 0, 
                    totalItem: article.price, 
                    price_base: article.price,
                    notes: articleNote, 
                    t_item: 'ART',
                    articleLocked: true
                };
                if (article.c_unmed === "1" && parseFloat(article.q_pesuni) > 0) {
                    newItem.wq_pesuni = parseFloat(article.q_pesuni);
                }
                const newItemsList = [...quotedItems, newItem];
                const nextIndex = newItemsList.length - 1;
                setSelectedQuotedItemIndex(nextIndex); 
                targetIndex = nextIndex;
                await updateAndCalculateRow(nextIndex, newItem, client.id, user?.empresa_activa?.id, newItemsList);
            }
        }

        // Limpiamos el activeRowIndex y cerramos modales
        setActiveRowIndex(null);
        setShowGlobalArticleModal(false);
        setShowArticleModal(false);

        // Posicionar foco en el campo Desc/Rec (discountSurcharge) del artículo recién ingresado
        if (targetIndex !== -1) {
            setTimeout(() => {
                const inputEl = document.getElementById(`input-${targetIndex}-discountSurcharge`);
                if (inputEl) {
                    inputEl.focus();
                    if (inputEl.select) inputEl.select();
                }
            }, 150);
        }
    };

    const handleItemChange = (index, field, value) => {
        if (!quotedItems[index]) return;
        const row = { ...quotedItems[index] };
        
        // Soporte para comas de teclado español como separador decimal
        if (typeof value === 'string' && ['price', 'quantity', 'discountSurcharge', 'boxes'].includes(field)) {
            value = value.replace(',', '.');
        }

        // REGLA DE SEGURIDAD: No permitimos valores negativos en campos numéricos clave
        let finalValue = value;
        if (['price', 'quantity', 'boxes'].includes(field)) {
            const num = parseFloat(value);
            if (!isNaN(num) && num < 0) {
                finalValue = 0; // Si es negativo, lo reseteamos a cero
            }
        }

        // REGLA: Precio máximo 8 enteros y 3 decimales (XXXXXXXX.DDD)
        if (field === 'price') {
            const regex = /^\d{0,8}(\.\d{0,3})?$/;
            if (!regex.test(value)) {
                return; // Bloqueamos la entrada inválida
            }
        }
        
        // REGLA: Cantidad y Bultos máximo 6 enteros y 2 decimales (XXXXXX.DD)
        if (field === 'quantity' || field === 'boxes') {
            const regex = /^\d{0,6}(\.\d{0,2})?$/;
            if (!regex.test(value)) {
                return; // Bloqueamos la entrada inválida
            }
        }
        
        // REGLA: El código es siempre mayúscula y tiene un máximo de 15 caracteres
        if (field === 'id') {
            finalValue = value.toUpperCase().substring(0, 15);
        }
        
        row[field] = finalValue;
        if (field === 'price') {
            row.price_base = parseFloat(finalValue) || 0;
        }

        // Búsqueda en tiempo real (Autocompletado) mientras escribís el CÓDIGO
        if (field === 'id') {
            setActiveRowIndex(index);
            // Actualizar timestamp en CADA carácter: mide tiempo desde último char hasta Enter
            if (value.length > 0) {
                scannerStartTimeRef.current[index] = Date.now();
                idValueRef.current[index] = finalValue; // Ref sincrónico: siempre actualizado aunque React no haya re-renderizado
            } else {
                delete scannerStartTimeRef.current[index];
                delete idValueRef.current[index];
            }
        }

        // Si cambian los bultos, recalculamos la cantidad de artículos
        if (field === 'boxes') {
            const bultos = parseFloat(finalValue) || 0;
            
            let unxbu = parseFloat(row.bulto);
            if (isNaN(unxbu) || unxbu <= 0) unxbu = 1;
            
            let medid = parseFloat(row.q_medid);
            if (isNaN(medid) || medid <= 0) medid = 1;
            
            // Artículos = Bultos x q_unxbu x q_medid
            const newQuantity = bultos * unxbu * medid;
            
            // Solo actualizamos si cambió, y redondeamos para evitar bugs de coma flotante
            row.quantity = Math.round(newQuantity * 100) / 100;
        }

        // Si cambian los artículos manualmente, ponemos los bultos en 0
        if (field === 'quantity') {
            row.boxes = 0;
        }

        // Si cambias Precio, Cantidad o Desc/Rec, hay que recalcular el "Total Item"
        if (field === 'price' || field === 'quantity' || field === 'discountSurcharge' || field === 'boxes') {
            const price = parseFloat(row.price) || 0;
            const qty = parseFloat(row.quantity) || 0;
            const disc = parseFloat(row.discountSurcharge) || 0;
            // FÓRMULA: (Precio * Cantidad) + Recargo o - Descuento
            row.totalItem = (price * qty) * (1 + (disc / 100));

            // LÓGICA DE PESO (Kg): Si c_unmed = "1" y q_pesuni > 0
            const pesuni = parseFloat(row.q_pesuni) || 0;
            if (row.c_unmed === "1" && pesuni > 0) {
                row.wq_pesuni = qty * pesuni;
            } else {
                row.wq_pesuni = 0;
            }
        }
        const updated = [...quotedItems];
        updated[index] = row;
        setQuotedItems(updated);
    };

    /**
     * handleKeyDown: CEREBRO DE LA NAVEGACIÓN POR TECLADO
     * Maneja el paso de foco entre campos, escáner de barras y bloqueos de seguridad.
     */
    const handleKeyDown = async (e, index, field) => {
        // Soporte para teclados móviles que pueden no enviar 'Enter' propiamente
        if (e.key === 'Enter' || e.keyCode === 13 || e.key === 'Next' || e.key === 'Go') {
            e.preventDefault();

            // 2. LÓGICA DE CÓDIGO/ESCÁNER: Búsqueda manual (cuando no hay o no se eligió sugerencia)
            if (field === 'id') {
                // Usar ref sincrónico en lugar del estado (que puede estar desactualizado con la pistola)
                const enteredId = idValueRef.current[index] || quotedItems[index].id;
                if (enteredId) {
                    // 1. Validar que no sea "LIT" (si el usuario tipea LIT, tal vez abrir modal literal?)
                    // Por ahora sigamos con la búsqueda de artículo
                    
                    // 2. Buscar por c_artic (id) O por c_barra (barcode)
                    let foundArticle = globalArticles.find(a => a.id.toLowerCase() === enteredId.toLowerCase());
                    if (!foundArticle) {
                        foundArticle = globalArticles.find(a => a.c_barra && a.c_barra.toLowerCase() === enteredId.toLowerCase());
                    }

                    // Si no está en los artículos precargados en memoria, buscar en el backend
                    if (!foundArticle) {
                        try {
                            const fetchRes = await axios.get(`http://127.0.0.1:8000/api/articulos/?search=${encodeURIComponent(enteredId)}`);
                            const searchResults = fetchRes.data.results || (Array.isArray(fetchRes.data) ? fetchRes.data : []);
                            const exact = searchResults.find(a => a.c_artic.toLowerCase() === enteredId.toLowerCase() || (a.c_barra && a.c_barra.toLowerCase() === enteredId.toLowerCase()));
                            if (exact) {
                                const listPrice = pricesByArtic[exact.c_artic];
                                const rubro = rubros.find(r => r.c_rubro === exact.c_rubro);
                                foundArticle = {
                                    id: exact.c_artic,
                                    name: exact.d_artic,
                                    price: listPrice !== undefined && listPrice !== null ? parseFloat(listPrice) : parseFloat(exact.i_conta || 0),
                                    bulto: exact.q_unxbu !== undefined && exact.q_unxbu !== null ? parseFloat(exact.q_unxbu) : 1,
                                    q_medid: exact.q_medid !== undefined && exact.q_medid !== null ? parseFloat(exact.q_medid) : 1,
                                    description: exact.d_arti2 || exact.d_artic,
                                    notes: "",
                                    c_iva: exact.c_iva,
                                    c_barra: exact.c_barra || "",
                                    c_situa: exact.c_situa,
                                    c_vendev: (exact.c_vendev && exact.c_vendev.trim()) ? exact.c_vendev : "V",
                                    rubroId: exact.c_rubro,
                                    rubroName: rubro ? rubro.d_rubro : 'Sin Rubro',
                                    t_artic: exact.t_artic,
                                    i_costo: exact.i_costo !== undefined && exact.i_costo !== null ? parseFloat(exact.i_costo) : 0,
                                    i_suger: exact.i_suger,
                                    c_moned: exact.c_moned,
                                    c_descli: exact.c_descli,
                                    c_varian: exact.c_varian,
                                    c_ubica: exact.c_ubica,
                                    d_pasil: exact.d_pasil,
                                    d_estan: exact.d_estan,
                                    d_posic: exact.d_posic,
                                    d_imagen: exact.d_imagen,
                                    d_pathimg: exact.d_pathimg,
                                    d_undisky: exact.d_undisky,
                                    c_unmed: exact.c_unmed,
                                    c_ingbr: exact.c_ingbr,
                                    c_impuc: exact.c_impuc,
                                    l_trazar: exact.l_trazar,
                                    q_pesuni: exact.q_pesuni
                                };
                            }
                        } catch (err) {
                            console.error("Error buscando artículo en backend:", err);
                        }
                    }

                    if (foundArticle) {
                        // 3. Verificar situación (c_situa <> 9)
                        if (parseInt(foundArticle.c_situa, 10) === 9) {
                            Swal.fire({
                                title: 'Producto Inhabilitado',
                                text: 'Producto INHABILITADO. Verifique !!',
                                icon: 'warning',
                                confirmButtonColor: '#3085d6',
                                confirmButtonText: 'Aceptar'
                            });
                            return;
                        }

                        const allowSumPro = user?.empresa_activa?.l_sumapro !== false;
                        const existingIndex = allowSumPro ? quotedItems.findIndex((item, idx) => item.id.toLowerCase() === foundArticle.id.toLowerCase() && idx !== index) : -1;
                        
                        if (existingIndex !== -1) {
                            const updated = [...quotedItems];
                            updated[existingIndex].quantity = (parseFloat(updated[existingIndex].quantity) || 0) + 1;
                            const price = parseFloat(updated[existingIndex].price) || 0;
                            const disc = parseFloat(updated[existingIndex].discountSurcharge) || 0;
                            updated[existingIndex].totalItem = (price * updated[existingIndex].quantity) * (1 + (disc / 100));
                            
                            updated[index].id = '';
                            updated[index].name = '';
                            updated[index].price = 0;
                            updated[index].totalItem = 0;
                            
                            await updateAndCalculateRow(existingIndex, updated[existingIndex], client.id, user?.empresa_activa?.id, updated);
                            setSelectedQuotedItemIndex(existingIndex);
                            
                            // FOCO: Mover a la fila existente
                            setTimeout(() => {
                                const nextElement = document.getElementById(`input-${existingIndex}-price`);
                                if (nextElement) {
                                    nextElement.focus();
                                    if (nextElement.select) nextElement.select();
                                }
                            }, 50);
                            return;
                        } else {
                            // Detectar si fue la pistola: tiempo desde último char hasta Enter
                            const elapsed = Date.now() - (scannerStartTimeRef.current[index] || 0);
                            const isScanner = elapsed < 150;
                            delete scannerStartTimeRef.current[index];

                            const updated = [...quotedItems];
                            updated[index] = { 
                                ...updated[index],
                                ...foundArticle, // Copiar todos los campos (t_artic, i_suger, c_moned, c_iva, etc.)
                                id: foundArticle.id,           // Usar el ID oficial del artículo encontrado
                                name: foundArticle.name, 
                                notes: updated[index].notes || '',
                                price: foundArticle.price, 
                                price_base: foundArticle.price,
                                bulto: foundArticle.bulto,
                                q_medid: foundArticle.q_medid,
                                totalItem: foundArticle.price * (updated[index].quantity || 1),
                                t_item: 'ART',
                                articleLocked: true
                            };

                            // LÓGICA DE PESO INICIAL
                            if (foundArticle.c_unmed === "1" && parseFloat(foundArticle.q_pesuni) > 0) {
                                updated[index].wq_pesuni = (updated[index].quantity || 1) * parseFloat(foundArticle.q_pesuni);
                            }

                            if (isScanner) {
                                // MODO PISTOLA: cargar artículo y saltar directo a la siguiente línea
                                const newRows = [...updated, { id: '', name: '', price: 0, quantity: 1, boxes: 0, discountSurcharge: 0, totalItem: 0, notes: '', t_item: 'ART', c_vendev: 'V' }];
                                await updateAndCalculateRow(index, updated[index], client.id, user?.empresa_activa?.id, newRows);
                                const nextRowIndex = index + 1;
                                setSelectedQuotedItemIndex(nextRowIndex);
                                setTimeout(() => {
                                    const nextElement = document.getElementById(`input-${nextRowIndex}-id`);
                                    if (nextElement) {
                                        nextElement.focus();
                                        if (nextElement.select) nextElement.select();
                                    }
                                }, 50);
                            } else {
                                // MODO MANUAL: ir al siguiente campo (Desc/Rec)
                                await updateAndCalculateRow(index, updated[index], client.id, user?.empresa_activa?.id, updated);
                                setTimeout(() => {
                                    const nextElement = document.getElementById(`input-${index}-discountSurcharge`);
                                    if (nextElement) {
                                        nextElement.focus();
                                        if (nextElement.select) nextElement.select();
                                    }
                                }, 50);
                            }
                            return; 
                        }
                    } else {
                        // Si no se encuentra por código ni código de barras, abrimos el modal global con lo que escribió
                        handleOpenGlobalModal(enteredId, index);
                        return;
                    }
                } else {
                    return;
                }
            }

            // 3. NAVEGACIÓN SECUENCIAL: Producto → Desc/Rec → Bultos → Cantidad → nueva línea
            if (field === 'price') {
                const nextElement = document.getElementById(`input-${index}-discountSurcharge`);
                if (nextElement) {
                    nextElement.focus();
                    if (nextElement.select) nextElement.select();
                }
                return;
            }

            const fields = ['id', 'discountSurcharge', 'boxes', 'quantity'];
            const currentFieldIndex = fields.indexOf(field);
            
            if (currentFieldIndex !== -1 && currentFieldIndex < fields.length - 1) {
                const nextField = fields[currentFieldIndex + 1];
                const nextElement = document.getElementById(`input-${index}-${nextField}`);
                if (nextElement) {
                    nextElement.focus();
                    if (nextElement.select) nextElement.select();
                }
            } else if (index < quotedItems.length - 1) {
                const nextRowIndex = index + 1;
                const updatedList = quotedItems.map((it, idx) => idx === index || Boolean(it.name) ? { ...it, articleLocked: true } : it);
                setQuotedItems(updatedList);
                setSelectedQuotedItemIndex(nextRowIndex);
                setTimeout(() => {
                    const nextElement = document.getElementById(`input-${nextRowIndex}-id`);
                    if (nextElement) {
                        nextElement.focus();
                        if (nextElement.select) nextElement.select();
                    }
                }, 50);
            } else if (field === 'discountSurcharge' || field === 'notes' || field === 'quantity') {
                const updatedPreviousRows = quotedItems.map(item => {
                    if (item.id || item.name || parseFloat(item.price) > 0 || parseFloat(item.totalItem) > 0) {
                        return { ...item, articleLocked: true };
                    }
                    return item;
                });
                const newRows = [...updatedPreviousRows, { id: '', name: '', price: 0, quantity: 1, boxes: 0, discountSurcharge: 0, totalItem: 0, notes: '', t_item: 'ART', c_vendev: 'V' }];
                setQuotedItems(newRows);
                setSelectedQuotedItemIndex(index + 1);
                setTimeout(() => {
                    const nextElement = document.getElementById(`input-${index + 1}-id`);
                    if (nextElement) {
                        nextElement.focus();
                        if (nextElement.select) nextElement.select();
                    }
                }, 50);
            }
        }
    };

    const handleItemBlur = async (index, field) => {
        if (['price', 'quantity', 'discountSurcharge', 'boxes'].includes(field)) {
            const currentItems = quotedItemsRef.current || quotedItems;
            const row = currentItems[index];
            if (row && row.id && row.id !== 'LITERAL' && row.t_item !== 'LIT') {
                const currentCalcKey = `${client.id || ''}_${row.id}_${row.quantity}_${row.price_base !== undefined ? row.price_base : row.price}_${row.discountSurcharge}`;
                if (row._lastCalculatedKey === currentCalcKey) {
                    return;
                }
                updateAndCalculateRow(index, row);
            }
        }
    };

    const handleItemFocus = (index, field) => {
        setFocusedInput(`input-${index}-${field}`);
        
        // Refrescamos artículos y precios si vamos a buscar uno nuevo (celda de código vacía)
        if (field === 'id' && !quotedItems[index].id && !isGlobalLoading && (!rawGlobalArticles || rawGlobalArticles.length === 0)) {
            setIsGlobalLoading(true);
            axios.get('http://127.0.0.1:8000/api/articulos/?page=1&page_size=100')
                .then(res => {
                    const data = res.data;
                    setRawGlobalArticles(data.results || (Array.isArray(data) ? data : []));
                    setIsGlobalLoading(false);
                })
                .catch(err => {
                    console.error('Error fetching global articles', err);
                    setIsGlobalLoading(false);
                });
            
            if (list.id) {
                axios.get(`http://127.0.0.1:8000/api/precios/?n_lista=${list.id}`)
                    .then(res => {
                        const priceMap = {};
                        res.data.forEach(p => {
                            priceMap[p.c_artic] = p.i_conta;
                        });
                        setPricesByArtic(priceMap);
                    })
                    .catch(err => console.error("Error obteniendo precios:", err));
            }
        }
    };

    const handleLiteralKeyDown = (e, field) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
            e.preventDefault();
            if (field === 'product') {
                document.getElementById('literal-description-input')?.focus();
            } else if (field === 'description') {
                document.getElementById('literal-price-input')?.focus();
            } else if (field === 'price') {
                document.getElementById('literal-quantity-input')?.focus();
            } else if (field === 'quantity') {
                handleAddLiteral();
            }
        }
    };

    const handleAddLiteral = () => {
        if (!literalItem.description) return;
        const price = parseFloat(literalItem.price) || 0;
        const qty = parseFloat(literalItem.quantity) || 1;
        const prodCode = (literalItem.id || 'LITERAL').toUpperCase();
        
        const existingIndex = quotedItems.findIndex(item => item.id === prodCode && item.name === literalItem.description);
        
        if (existingIndex !== -1) {
            const updated = [...quotedItems];
            updated[existingIndex].quantity = (parseFloat(updated[existingIndex].quantity) || 0) + qty;
            updated[existingIndex].totalItem = updated[existingIndex].price * updated[existingIndex].quantity;
            setQuotedItems(updated);
            setSelectedQuotedItemIndex(existingIndex);
        } else {
            const lastItemIndex = quotedItems.length - 1;
            if (lastItemIndex >= 0 && !quotedItems[lastItemIndex].id && !quotedItems[lastItemIndex].name) {
                const updated = [...quotedItems];
                updated[lastItemIndex] = { id: prodCode, name: literalItem.description, price: price, price_base: price, quantity: qty, boxes: 0, discountSurcharge: 0, totalItem: price * qty, notes: '', t_item: 'LIT', c_vendev: 'V' };
                setQuotedItems(updated);
                setSelectedQuotedItemIndex(lastItemIndex);
            } else {
                setQuotedItems([
                    ...quotedItems, 
                    { id: prodCode, name: literalItem.description, price: price, price_base: price, quantity: qty, boxes: 0, discountSurcharge: 0, totalItem: price * qty, notes: '', t_item: 'LIT', c_vendev: 'V' }
                ]);
                setSelectedQuotedItemIndex(quotedItems.length);
            }
        }
        
        setShowLiteralModal(false);
        setLiteralItem({ id: '', description: '', price: '', quantity: 1, isExtended: false });
    };

    const handleAddLine = () => {
        const lastItemIndex = quotedItems.length - 1;
        if (lastItemIndex >= 0 && !quotedItems[lastItemIndex].id && !quotedItems[lastItemIndex].name) {
            setSelectedQuotedItemIndex(lastItemIndex);
            setTimeout(() => {
                const nextElement = document.getElementById(`input-${lastItemIndex}-id`);
                if (nextElement) {
                    nextElement.focus();
                    if (nextElement.select) nextElement.select();
                }
            }, 50);
            return;
        }

        const updatedPreviousRows = quotedItems.map(item => {
            if (item.id || item.name || parseFloat(item.price) > 0 || parseFloat(item.totalItem) > 0) {
                return { ...item, articleLocked: true };
            }
            return item;
        });

        const newRow = { id: '', name: '', price: 0, quantity: 1, boxes: 0, discountSurcharge: 0, totalItem: 0, notes: '', t_item: 'ART', c_vendev: 'V' };
        const newIndex = updatedPreviousRows.length;
        setQuotedItems([...updatedPreviousRows, newRow]);
        setSelectedQuotedItemIndex(newIndex);
        setTimeout(() => {
            const nextElement = document.getElementById(`input-${newIndex}-id`);
            if (nextElement) {
                nextElement.focus();
                if (nextElement.select) nextElement.select();
            }
        }, 50);
    };

    const grandTotal = quotedItems.reduce((acc, item) => acc + (parseFloat(item.totalItem) || 0), 0);

    // Sumatoria de Impuestos a Nivel Renglón
    const totalIVA1 = quotedItems.reduce((acc, item) => {
        const ivaimp = parseFloat(item.i_ivaimp) || 0;
        const iva1 = item.i_iva1 !== undefined ? parseFloat(item.i_iva1) : (ivaimp > 0 ? 0 : parseFloat(item.i_iva));
        return acc + (iva1 || 0);
    }, 0);
    const totalIVA2 = quotedItems.reduce((acc, item) => acc + (parseFloat(item.i_iva2) || 0), 0);
    const totalIVA3 = quotedItems.reduce((acc, item) => acc + (parseFloat(item.i_iva3) || 0), 0);
    const totalIVAimp = quotedItems.reduce((acc, item) => acc + (parseFloat(item.i_ivaimp) || 0), 0);
    const totalIVA = totalIVA1 + totalIVA2 + totalIVAimp;
    const totalIIBB = quotedItems.reduce((acc, item) => acc + (parseFloat(item.i_ingbr) || 0), 0);
    const totalMunic = quotedItems.reduce((acc, item) => acc + (parseFloat(item.i_munic) || 0), 0);
    const totalImpub = quotedItems.reduce((acc, item) => acc + (parseFloat(item.i_impub) || 0), 0);
    const totalImint = quotedItems.reduce((acc, item) => acc + (parseFloat(item.i_imint) || 0), 0);
    const totalRG212 = quotedItems.reduce((acc, item) => acc + (parseFloat(item.i_rg212) || 0), 0);

    // _LDISCR = IIF(SPAR010.C_MARC1="S", .T., .F.) en SPAR010 con C_PARAM="IC " y secuencia MCLI000.C_IVA
    const clientIvaParam = useMemo(() => {
        const foundClient = dbClients.find(cl => String(cl.c_clien) === String(client.id));
        const c_iva = foundClient?.c_iva ?? client?.c_iva;
        if (c_iva === undefined || c_iva === null || c_iva === '') return null;
        return ivaConditions.find(ic => parseInt(ic.c_secue, 10) === parseInt(c_iva, 10)) || null;
    }, [dbClients, client.id, client.c_iva, ivaConditions]);

    const isDiscriminated = useMemo(() => {
        if (clientIvaParam) {
            return String(clientIvaParam.c_marc1 || '').trim().toUpperCase() === 'S';
        }
        return Boolean(client.vatStatus && client.vatStatus.toLowerCase().includes('inscripto'));
    }, [clientIvaParam, client.vatStatus]);

    const subtotalNeto = isDiscriminated 
        ? grandTotal 
        : (grandTotal - (totalIVA + totalMunic + totalImpub + totalImint));

    const finalGrandTotal = isDiscriminated
        ? (grandTotal + totalIVA + totalIVA3 + totalIIBB + totalMunic + totalImpub + totalImint + totalRG212)
        : (grandTotal + totalIVA3 + totalIIBB + totalRG212);

    // Use database price lists instead of mock data
    const lists = dbLists;

    const filteredLists = useMemo(() => lists.filter(l =>
        String(l.n_lista).includes(listSearch) ||
        (l.d_obser && l.d_obser.toLowerCase().includes(listSearch.toLowerCase()))
    ), [lists, listSearch]);

    const paginatedLists = useMemo(() => {
        const startIndex = (listPage - 1) * itemsPerPage;
        return filteredLists.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredLists, listPage]);

    const isHeaderComplete = client.id && client.name && list.id && list.description && paymentCondition;
    const isInitialLoading = isLoadingClients || isLoadingLists || isLoadingRubros;

    if (isInitialLoading) {
        return (
            <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                <div className="card shadow-sm border-0 text-center p-5 rounded-4" style={{ maxWidth: '440px', width: '100%', background: '#ffffff' }}>
                    <div className="mb-4 d-flex justify-content-center align-items-center">
                        <div className="spinner-border text-primary" style={{ width: '3.5rem', height: '3.5rem', borderWidth: '0.3em' }} role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                    <h5 className="fw-bold text-primary mb-2">Cargando datos del cotizador</h5>
                    <p className="text-muted small mb-0">
                        Por favor aguarde un momento mientras cargamos la información de clientes, listas de precio y rubros...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4 pb-5 text-start px-4">
            {/* TÍTULO Y BOTÓN DE PLEGAR CABECERA */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <h2 className="mb-0"><i className="bi bi-calculator me-2"></i>Cotizador</h2>
                    {editingDraftId && (
                        <span className="badge bg-warning text-dark fs-6 px-3 py-2 shadow-sm border border-warning-subtle">
                            <i className="bi bi-pencil-square me-2"></i>
                            EDITANDO BORRADOR: <strong>{editingDraftLabel}</strong>
                        </span>
                    )}
                </div>
                <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
                    title={isHeaderExpanded ? "Ocultar cabecera" : "Mostrar cabecera"}
                    className="fw-bold shadow-sm"
                >
                    <i className={`bi bi-chevron-${isHeaderExpanded ? 'up' : 'down'} me-2`}></i>
                    {isHeaderExpanded ? 'Plegar Cabecera' : 'Desplegar Cabecera'}
                </Button>
            </div>

            {/* SECCIÓN 1: DATOS GENERALES (CLIENTE, LISTA, PAGO) */}
            <Collapse in={isHeaderExpanded}>
                <div>
                    <div className="card shadow-sm border-0">
                        <div className="card-body p-4">
                            <Row className="g-2 mb-2">
                                <Col md={4} lg={2}>
                                    <InputGroup size="sm" className="shadow-sm">
                                        <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '70px' }}>Cliente<span className="text-danger ms-1">*</span></InputGroup.Text>
                                        <Form.Control 
                                            id="header-client-id"
                                            type="text" 
                                            placeholder="N°" 
                                            maxLength={6} 
                                            className={`text-center ${ (isHeaderConfirmed || isClientLocked) ? 'bg-secondary-subtle' : '' }`}
                                            value={client.id} 
                                            onChange={handleClientChange} 
                                            onFocus={(e) => handleSafeFocus('header-client-id', null, e)}
                                            onBlur={(e) => handleSafeBlur(() => validateClientVendorAccess(e.target.value))}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.keyCode === 13 || e.key === 'Next' || e.key === 'Go') {
                                                    e.preventDefault();
                                                    if (!validateClientVendorAccess(client.id)) return;
                                                    const next = document.getElementById('header-list-id');
                                                    if (next) { next.focus(); next.select(); }
                                                }
                                            }}
                                            disabled={isHeaderConfirmed || isClientLocked} 
                                            enterKeyHint="next"
                                        />
                                        <Button 
                                            variant="outline-primary" 
                                            onClick={handleShowClientModal} 
                                            disabled={isHeaderConfirmed || isClientLocked}
                                        >
                                            <i className="bi bi-search"></i>
                                        </Button>
                                    </InputGroup>
                                </Col>
                                <Col md={8} lg={3}>
                                    <InputGroup size="sm" className="shadow-sm">
                                        <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '65px' }}>Nombre</InputGroup.Text>
                                        <Form.Control 
                                            type="text" 
                                            disabled={isHeaderConfirmed || isClientLocked} 
                                            className={(isHeaderConfirmed || isClientLocked) ? 'bg-secondary-subtle' : 'bg-light'} 
                                            placeholder="..." 
                                            value={client.name} 
                                            readOnly
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={7} lg={3}>
                                    <InputGroup size="sm" className="shadow-sm">
                                        <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '65px' }}>Dom</InputGroup.Text>
                                        <Form.Control 
                                            type="text" 
                                            disabled={isHeaderConfirmed || isClientLocked} 
                                            className={(isHeaderConfirmed || isClientLocked) ? 'bg-secondary-subtle' : 'bg-light'} 
                                            placeholder="..." 
                                            value={deliveryAddress || client.address} 
                                            readOnly
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={5} lg={2}>
                                    <InputGroup size="sm" className="shadow-sm">
                                        <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '45px' }}>IVA</InputGroup.Text>
                                        <Form.Control 
                                            type="text" 
                                            disabled={isHeaderConfirmed || isClientLocked} 
                                            className={(isHeaderConfirmed || isClientLocked) ? 'bg-secondary-subtle' : 'bg-light'} 
                                            value={client.vatStatus} 
                                            placeholder="Cond..." 
                                            readOnly
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={12} lg={2}>
                                    <InputGroup size="sm" className="shadow-sm h-100">
                                        <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '60px' }}>Fecha</InputGroup.Text>
                                        <Form.Control type="date" defaultValue={new Date().toISOString().substring(0, 10)} readOnly={isHeaderConfirmed} />
                                    </InputGroup>
                                </Col>
                            </Row>
                            
                            <Row className="g-2 mb-4">
                                <Col md={4} lg={3}>
                                    <InputGroup size="sm" className="shadow-sm h-100">
                                        <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '105px' }}>Comprobante</InputGroup.Text>
                                        <Form.Control type="text" value="Cotización" readOnly className="bg-light" />
                                    </InputGroup>
                                </Col>
                                <Col md={4} lg={2}>
                                    <InputGroup size="sm" className="shadow-sm">
                                        <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '70px' }}>Lista<span className="text-danger ms-1">*</span></InputGroup.Text>
                                        <Form.Control 
                                            id="header-list-id"
                                            type="text" 
                                            placeholder="N°" 
                                            maxLength={6} 
                                            className="text-center" 
                                            value={list.id} 
                                            onChange={handleListChange} 
                                            onFocus={(e) => handleSafeFocus('header-list-id', null, e)}
                                            onBlur={() => handleSafeBlur()}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.keyCode === 13 || e.key === 'Next' || e.key === 'Go') {
                                                    e.preventDefault();
                                                    const next = document.getElementById('header-accept-button');
                                                    if (next) { next.focus(); }
                                                }
                                            }}
                                            disabled={isHeaderConfirmed} 
                                            enterKeyHint="next"
                                        />
                                        <Button variant="outline-primary" onClick={handleShowListModal} disabled={isHeaderConfirmed}><i className="bi bi-search"></i></Button>
                                    </InputGroup>
                                </Col>
                                <Col md={4} lg={3}>
                                    <InputGroup size="sm" className="shadow-sm">
                                        <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '65px' }}>Desc</InputGroup.Text>
                                        <Form.Control type="text" readOnly className="bg-light" placeholder="..." value={list.description} />
                                    </InputGroup>
                                </Col>
                                <Col md={4} lg={2}>
                                    <InputGroup size="sm" className="shadow-sm">
                                        <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '55px' }}>Pago<span className="text-danger ms-1">*</span></InputGroup.Text>
                                        <Form.Select 
                                            value={paymentCondition} 
                                            onChange={(e) => setPaymentCondition(e.target.value !== '' ? parseInt(e.target.value, 10) : '')} 
                                            disabled={isHeaderConfirmed}
                                        >
                                            <option value="">...</option>
                                            {paymentConditions.map(p => (
                                                <option key={p.c_secue} value={parseInt(p.c_secue, 10)}>{p.d_secue}</option>
                                            ))}
                                        </Form.Select>
                                    </InputGroup>
                                </Col>
                                <Col md={8} lg={2} className="d-flex align-items-center">
                            {!isHeaderConfirmed ? (
                                <Button 
                                    id="header-accept-button"
                                    variant="success" 
                                    size="sm" 
                                    className="fw-bold w-100 h-100 shadow-sm" 
                                    disabled={!isHeaderComplete} 
                                    onClick={() => { 
                                        setIsHeaderConfirmed(true); 
                                        setIsClientLocked(true); 
                                        // Si la tabla está vacía, agregamos el primer renglón listo para cargar
                                        if (quotedItems.length === 0) {
                                            setQuotedItems([{ id: '', name: '', price: 0, quantity: 1, boxes: 0, discountSurcharge: 0, totalItem: 0, notes: '', t_item: 'ART', c_vendev: 'V' }]);
                                            setSelectedQuotedItemIndex(0);
                                        }
                                        // Enfocar el primer campo de código con un pequeño delay para que React renderice la tabla
                                        setTimeout(() => {
                                            const firstInput = document.getElementById('input-0-id');
                                            if (firstInput) {
                                                firstInput.focus();
                                                if (firstInput.select) firstInput.select();
                                            }
                                        }, 100);
                                    }}
                                >
                                    <i className="bi bi-check-circle me-1"></i> Aceptar
                                </Button>
                            ) : (
                                <Button variant="outline-secondary" size="sm" className="fw-bold w-100 h-100 shadow-sm" onClick={() => { setIsHeaderConfirmed(false); }}>
                                    <i className="bi bi-pencil-square me-1"></i> Editar
                                </Button>
                            )}
                        </Col>
                    </Row>
                        </div>
                    </div>
                </div>
            </Collapse>

            {/* TABLA Y CONTROLES */}
            <div 
                className="card shadow-sm border-0 mt-3"
                style={{ 
                    opacity: isHeaderConfirmed ? 1 : 0.4, 
                    pointerEvents: isHeaderConfirmed ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease-in-out'
                }}
            >
                <div className="card-body p-4">
                    <Row className="g-3">
                        {/* COLUMNA IZQUIERDA - RUBROS */}
                        {!isRubrosCollapsed && (
                            <Col md={3} lg={2} className="d-flex flex-column pe-lg-3 order-3 order-lg-1">
                                <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                                    <h6 className="text-muted fw-bold text-uppercase mb-0 flex-grow-1 text-center">Rubros</h6>
                                    <div className="d-flex gap-1">
                                        <Button variant="outline-secondary" size="sm" onClick={() => setShowRubroModal(true)} title="Buscar Rubro">
                                            <i className="bi bi-question-lg"></i>
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" onClick={() => setIsRubrosCollapsed(true)} title="Plegar Rubros">
                                            <i className="bi bi-chevron-left"></i>
                                        </Button>
                                    </div>
                                </div>
                                <div className="d-flex flex-column gap-2 flex-grow-1 overflow-auto pe-1" style={{ maxHeight: '420px' }}>
                                    {rubros.length > 0 ? rubros.map((r) => (
                                        <Button 
                                            key={r.c_rubro} 
                                            id={`rubro-btn-${r.c_rubro}`}
                                            variant={selectedRubro?.c_rubro === r.c_rubro ? 'primary' : 'outline-secondary'} 
                                            className={`w-100 rounded-pill shadow-sm text-truncate flex-shrink-0 ${selectedRubro?.c_rubro === r.c_rubro ? 'fw-bold border-2' : ''}`} 
                                            style={{ minHeight: '38px' }}
                                            onClick={() => handleSelectRubro(r)}
                                        >
                                            {r.d_rubro}
                                        </Button>
                                    )) : Array.from({ length: 10 }).map((_, i) => (
                                        <Button key={`ph-${i}`} variant="outline-secondary" disabled className="w-100 rounded-pill flex-shrink-0" style={{ minHeight: '38px', borderStyle: 'dashed' }}></Button>
                                    ))}
                                </div>
                            </Col>
                        )}

                        {/* COLUMNA CENTRAL - TABLA */}
                        <Col md={isRubrosCollapsed ? 12 : 9} lg={isRubrosCollapsed ? 11 : 9} className="order-2 order-lg-2">
                            {isRubrosCollapsed && (
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        className="fw-bold shadow-sm d-flex align-items-center"
                                        onClick={() => setIsRubrosCollapsed(false)}
                                        title="Ver Rubros"
                                    >
                                        <i className="bi bi-list me-2"></i>
                                        Ver Rubros
                                    </Button>
                                </div>
                            )}
                            <div className="table-responsive rounded border mb-3 shadow-sm bg-white">
                                <Table hover size="sm" className="mb-0 align-middle w-100" style={{ tableLayout: 'fixed', minWidth: '1300px' }}>
                                    <thead className="table-light" style={{ whiteSpace: 'nowrap' }}>
                                        <tr>
                                            <th style={{ width: '170px', minWidth: '170px' }}>Producto</th>
                                            <th style={{ width: '120px', minWidth: '120px' }}>P. Unitario</th>
                                            <th style={{ width: '100px', minWidth: '100px', textAlign: 'center' }}>Desc/Rec</th>
                                            <th style={{ width: '85px', minWidth: '85px', textAlign: 'center' }}>Bultos</th>
                                            <th style={{ width: '85px', minWidth: '85px', textAlign: 'center' }}>Cantidad</th>
                                            <th style={{ width: '150px', minWidth: '150px', textAlign: 'right', paddingRight: '1rem' }}>Total Item</th>
                                            <th style={{ width: 'auto', minWidth: '350px' }}>Descripción</th>
                                            <th style={{ width: '220px', minWidth: '220px' }}>Notas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quotedItems.map((item, i) => (
                                            <tr 
                                                key={i} 
                                                onClick={() => setSelectedQuotedItemIndex(i)}
                                                className={selectedQuotedItemIndex === i ? 'table-active border-primary' : ''}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    ...(selectedQuotedItemIndex !== i ? { '--bs-table-bg': i % 2 !== 0 ? '#fdf8ec' : '#ffffff' } : {})
                                                }}
                                            >
                                                <td style={{ height: '35px', verticalAlign: 'middle', position: 'relative' }}>
                                                    <InputGroup size="sm" className="flex-nowrap">
                                                        <Form.Control 
                                                            id={`input-${i}-id`}
                                                            type="text" 
                                                            maxLength={15}
                                                            className="text-uppercase fw-bold"
                                                            value={item.id} 
                                                            onChange={(e) => handleItemChange(i, 'id', e.target.value)} 
                                                            onKeyDown={(e) => handleKeyDown(e, i, 'id')}
                                                            onBlur={() => handleSafeBlur(() => handleItemBlur(i, 'id'))}
                                                            onFocus={(e) => { handleSafeFocus(`id-${i}`, i, e); handleItemFocus(i, 'id'); }}
                                                            autoComplete="off"
                                                            enterKeyHint="next"
                                                            disabled={item.articleLocked || Boolean(item.name)}
                                                        />
                                                        <Button 
                                                            variant="outline-secondary" 
                                                            onClick={() => {
                                                                handleOpenGlobalModal(item.id || '', i);
                                                            }}
                                                            title="Buscar Artículo Global"
                                                            disabled={item.articleLocked || Boolean(item.name)}
                                                        >
                                                            <i className="bi bi-search"></i>
                                                        </Button>
                                                        <Button 
                                                            variant="outline-info" 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenImageModal(item);
                                                            }}
                                                            title="Ver imagen del artículo"
                                                            disabled={!item.id && !item.name}
                                                        >
                                                            <i className="bi bi-eye"></i>
                                                        </Button>
                                                    </InputGroup>
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <Form.Control 
                                                        id={`input-${i}-price`}
                                                        size="sm" 
                                                        type="text" 
                                                        style={{ textAlign: 'right' }}
                                                        value={focusedInput === `price-${i}` ? item.price : formatCurrency(item.price)} 
                                                        disabled={item.articleLocked || !item.name}
                                                        onFocus={(e) => handleSafeFocus(`price-${i}`, i, e)}
                                                        onBlur={() => handleSafeBlur(() => handleItemBlur(i, 'price'))}
                                                        onChange={(e) => handleItemChange(i, 'price', e.target.value)} 
                                                        onKeyDown={(e) => handleKeyDown(e, i, 'price')}
                                                    />
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <InputGroup size="sm" className="flex-nowrap">
                                                        <Form.Control 
                                                            id={`input-${i}-discountSurcharge`}
                                                            className="text-center px-1" 
                                                            type="number" 
                                                            value={item.discountSurcharge || ''} 
                                                            disabled={!item.name}
                                                            onFocus={(e) => handleSafeFocus(`discountSurcharge-${i}`, i, e)}
                                                            onBlur={() => handleSafeBlur(() => handleItemBlur(i, 'discountSurcharge'))}
                                                            onChange={(e) => handleItemChange(i, 'discountSurcharge', e.target.value)} 
                                                            onKeyDown={(e) => handleKeyDown(e, i, 'discountSurcharge')}
                                                            placeholder="0" 
                                                            enterKeyHint="next"
                                                        />
                                                        <InputGroup.Text className="px-1">%</InputGroup.Text>
                                                    </InputGroup>
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <Form.Control 
                                                        id={`input-${i}-boxes`}
                                                        className="px-1" 
                                                        style={{ textAlign: 'center' }}
                                                        size="sm" 
                                                        type="text" 
                                                        value={focusedInput === `boxes-${i}` ? item.boxes : formatQuantity(item.boxes)} 
                                                        disabled={!item.name}
                                                        onFocus={(e) => handleSafeFocus(`boxes-${i}`, i, e)}
                                                        onBlur={() => handleSafeBlur(() => handleItemBlur(i, 'boxes'))}
                                                        onChange={(e) => handleItemChange(i, 'boxes', e.target.value)} 
                                                        onKeyDown={(e) => handleKeyDown(e, i, 'boxes')}
                                                        enterKeyHint="next"
                                                    />
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <Form.Control 
                                                        id={`input-${i}-quantity`}
                                                        className="px-1" 
                                                        style={{ textAlign: 'right' }}
                                                        size="sm" 
                                                        type="text" 
                                                        value={focusedInput === `quantity-${i}` ? item.quantity : formatQuantity(item.quantity)} 
                                                        disabled={!item.name}
                                                        onFocus={(e) => handleSafeFocus(`quantity-${i}`, i, e)}
                                                        onBlur={() => handleSafeBlur(() => handleItemBlur(i, 'quantity'))}
                                                        onChange={(e) => handleItemChange(i, 'quantity', e.target.value)} 
                                                        onKeyDown={(e) => handleKeyDown(e, i, 'quantity')}
                                                        enterKeyHint="next"
                                                    />
                                                </td>
                                                <td style={{ verticalAlign: 'middle', fontWeight: 'bold', paddingRight: '1rem', textAlign: 'right' }}>
                                                    {formatCurrency(item.totalItem)}
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <Form.Control size="sm" type="text" readOnly className="bg-light" value={item.name} />
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {selectedQuotedItemIndex === i ? (
                                                        <Form.Control 
                                                            id={`input-${i}-notes`}
                                                            size="sm" 
                                                            type="text" 
                                                            maxLength={55} 
                                                            value={item.notes || ''} 
                                                            disabled={!item.name}
                                                            onFocus={(e) => handleSafeFocus(`notes-${i}`, i, e)}
                                                            onBlur={() => handleSafeBlur()}
                                                            onChange={(e) => handleItemChange(i, 'notes', e.target.value)} 
                                                            onKeyDown={(e) => handleKeyDown(e, i, 'notes')}
                                                            placeholder="Nota..." 
                                                        />
                                                    ) : (
                                                        <span className="text-muted text-truncate d-inline-block w-100" style={{ maxHeight: '30px' }}>{item.notes || ''}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Filas vacías de relleno */}
                                        {Array.from({ length: Math.max(0, 8 - quotedItems.length) }).map((_, i) => {
                                            const actualIndex = quotedItems.length + i;
                                            return (
                                                <tr key={`empty-${i}`} style={{ '--bs-table-bg': actualIndex % 2 !== 0 ? '#fdf8ec' : '#ffffff' }}>
                                                    <td style={{ height: '35px' }}></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                            
                            <div className="d-flex justify-content-end align-items-center mt-1 py-1 px-3 bg-light rounded border w-100">
                                <div className="d-flex align-items-center">
                                {/* SECCIÓN DE TOTALES Y DESGLOSE IMPOSITIVO DILIGENTE */}
                                <div className="d-flex flex-column align-items-end mt-2 p-3 bg-white rounded border shadow-sm" style={{ minWidth: '320px' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-1 w-100 small text-muted">
                                        <span>Subtotal Neto:</span>
                                        <span>{formatCurrency(subtotalNeto)}</span>
                                    </div>
                                    {(totalIVA1 + totalIVA2) > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 w-100 small text-muted">
                                            <span>IVA Liquidado:</span>
                                            <span>{formatCurrency(totalIVA1 + totalIVA2)}</span>
                                        </div>
                                    )}
                                    {totalIVAimp > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 w-100 small text-muted">
                                            <span>IVA Tasa Dif. (10.5%):</span>
                                            <span>{formatCurrency(totalIVAimp)}</span>
                                        </div>
                                    )}
                                    {totalIVA3 > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 w-100 small text-muted">
                                            <span>Percepción IVA:</span>
                                            <span>{formatCurrency(totalIVA3)}</span>
                                        </div>
                                    )}
                                    {totalIIBB > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 w-100 small text-muted">
                                            <span>Percepción IIBB:</span>
                                            <span>{formatCurrency(totalIIBB)}</span>
                                        </div>
                                    )}
                                    {totalMunic > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 w-100 small text-muted">
                                            <span>Tasa Municipal:</span>
                                            <span>{formatCurrency(totalMunic)}</span>
                                        </div>
                                    )}
                                    {totalImpub > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 w-100 small text-muted">
                                            <span>Impuesto Publicidad:</span>
                                            <span>{formatCurrency(totalImpub)}</span>
                                        </div>
                                    )}
                                    {totalImint > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 w-100 small text-muted">
                                            <span>Impuestos Internos:</span>
                                            <span>{formatCurrency(totalImint)}</span>
                                        </div>
                                    )}
                                    {totalRG212 > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-1 w-100 small text-muted">
                                            <span>Percepción RG2126:</span>
                                            <span>{formatCurrency(totalRG212)}</span>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2 w-100">
                                        <span className="text-primary fw-bold fs-5">TOTAL:</span>
                                        <span className="text-primary fw-bold fs-5">
                                            {formatCurrency(finalGrandTotal)}
                                        </span>
                                    </div>
                                    <div className="text-muted small mt-2 w-100 text-end">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Empresa: <span className="fw-semibold">{user?.empresa_activa?.nombre || 'No seleccionada'}</span>
                                        {client.vatStatus && (
                                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                Condición IVA: <span className="fw-semibold">{client.vatStatus}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                </div>
                            </div>

                            {/* TABLA INFERIOR DE ARTICULOS DISPONIBLES AL HACER CLICK EN UN RUBRO */}
                            {selectedRubro && (
                                <div className="mt-3 bg-white p-3 rounded border shadow-sm">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold text-primary mb-0">
                                            <i className="bi bi-box-seam me-2"></i>Artículos disponibles en {selectedRubro.name || selectedRubro.d_rubro}
                                        </h6>
                                        <Button variant="outline-primary" size="sm" onClick={() => setShowArticleModal(true)} title="Buscar Artículo en Rubro" disabled={isLoadingArticles}>
                                            <i className="bi bi-search me-1"></i> Buscar
                                        </Button>
                                    </div>

                                    {isLoadingArticles ? (
                                        <div className="d-flex flex-column justify-content-center align-items-center py-5 bg-light rounded border border-dashed">
                                            <Spinner animation="border" variant="primary" className="mb-2" />
                                            <span className="text-muted fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                Cargando artículos de {selectedRubro.name || selectedRubro.d_rubro}...
                                            </span>
                                        </div>
                                    ) : (
                                        <div 
                                            className="table-responsive" 
                                            onScroll={handleRubroArticlesScroll}
                                            style={{ maxHeight: '250px', overflowY: 'auto' }}
                                        >
                                            <Table hover size="sm" className="mb-0 align-middle">
                                                <thead className="table-light sticky-top">
                                                    <tr>
                                                        <th>Código</th>
                                                        <th>Descripción</th>
                                                        <th>Precio</th>
                                                        <th className="text-end">Acción</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {availableArticles.length > 0 ? (
                                                        availableArticles.map((art) => (
                                                            <tr 
                                                                key={art.id}
                                                                onDoubleClick={() => handleAddArticle(art)}
                                                                onTouchEnd={(e) => {
                                                                    const now = Date.now();
                                                                    const lastTap = e.currentTarget._lastTap || 0;
                                                                    if (now - lastTap < 300) {
                                                                        handleAddArticle(art);
                                                                    }
                                                                    e.currentTarget._lastTap = now;
                                                                }}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <td className="fw-bold">{art.id}</td>
                                                                <td>{art.name}</td>
                                                                <td>{formatCurrency(art.price)}</td>
                                                                <td className="text-end pe-2">
                                                                    <div className="d-flex align-items-center justify-content-end gap-1">
                                                                        <Button 
                                                                            variant="outline-info" 
                                                                            size="sm" 
                                                                            className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                                                            style={{ width: '28px', height: '28px', padding: 0 }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleOpenImageModal(art);
                                                                            }}
                                                                            title="Ver imagen del artículo"
                                                                        >
                                                                            <i className="bi bi-eye"></i>
                                                                        </Button>
                                                                        <Button 
                                                                            variant="outline-success" 
                                                                            size="sm" 
                                                                            className="shadow-sm"
                                                                            onClick={() => handleAddArticle(art)}
                                                                            title="Agregar artículo"
                                                                        >
                                                                            <i className="bi bi-cart-plus me-1"></i>Agregar
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="4" className="text-center text-muted py-4">
                                                                <i className="bi bi-info-circle me-2"></i>
                                                                No hay artículos disponibles para este rubro
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                            {isLoadingMoreRubroArticles && (
                                                <div className="text-center py-2 bg-light border-top d-flex align-items-center justify-content-center">
                                                    <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                                    <span className="text-muted small">Cargando más artículos...</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Col>

                        {/* COLUMNA BOTONES (Arriba en móvil/tablet, al costado derecho en desktop) */}
                        <Col md={12} lg={1} className="d-flex flex-column flex-lg-column justify-content-between ps-lg-2 mb-3 mb-lg-0 order-1 order-lg-3">
                            <div className="d-flex flex-row flex-lg-column gap-2 flex-wrap align-items-center align-items-lg-stretch">
                                <Button 
                                    variant={deliveryAddressOption !== 'fiscal' ? "primary" : "outline-primary"} 
                                    className="fw-bold shadow-sm px-1" 
                                    style={{ fontSize: '0.8rem' }} 
                                    disabled={!client.id}
                                    onClick={() => {
                                        setTempDeliveryOption(deliveryAddressOption || 'fiscal');
                                        setTempManualAddress(deliveryAddressOption === 'manual' ? (manualDeliveryAddress || '') : '');
                                        setShowDeliveryAddressModal(true);
                                    }}
                                    title="Seleccionar o ingresar dirección de entrega"
                                >
                                    <i className="bi bi-truck me-1"></i>Dir. Entrega
                                </Button>
                                <Button variant="outline-primary" className="fw-bold shadow-sm px-1" style={{ fontSize: '0.8rem' }} onClick={() => setShowGeneralNoteModal(true)}>
                                    <i className="bi bi-journal-text me-1"></i>Notas generales
                                </Button>
                                <Button variant="outline-primary" className="fw-bold shadow-sm px-1" style={{ fontSize: '0.8rem' }} onClick={handleOpenGlobalModal}>
                                    <i className="bi bi-search me-1"></i>Buscador de artículos
                                </Button>
                                <Button 
                                    variant="outline-info" 
                                    className="fw-bold shadow-sm px-1" 
                                    style={{ fontSize: '0.8rem' }} 
                                    onClick={handleOpenImageModalForSelected}
                                    disabled={selectedQuotedItemIndex === null || (!quotedItems[selectedQuotedItemIndex]?.id && !quotedItems[selectedQuotedItemIndex]?.name)}
                                    title="Ver imagen del artículo seleccionado"
                                >
                                    <i className="bi bi-eye me-1"></i>Ver Imagen
                                </Button>
                                <Button 
                                    variant="outline-success" 
                                    className="fw-bold shadow-sm px-1" 
                                    style={{ fontSize: '0.8rem' }} 
                                    onClick={handleAddLine} 
                                    disabled={quotedItems.length > 0 && !quotedItems[quotedItems.length - 1]?.id && !quotedItems[quotedItems.length - 1]?.name}
                                    title={quotedItems.length > 0 && !quotedItems[quotedItems.length - 1]?.id && !quotedItems[quotedItems.length - 1]?.name ? "Ya existe una línea en blanco al final" : "Agregar una nueva línea a la grilla"}
                                >
                                    <i className="bi bi-plus-circle me-1"></i>Agregar línea
                                </Button>
                                <Button 
                                    variant="outline-danger" 
                                    className="fw-bold shadow-sm px-1" 
                                    style={{ fontSize: '0.8rem' }} 
                                    onClick={() => { if (selectedQuotedItemIndex !== null) requestDeleteRow(selectedQuotedItemIndex); }} 
                                    disabled={selectedQuotedItemIndex === null || (!quotedItems[selectedQuotedItemIndex]?.id && !quotedItems[selectedQuotedItemIndex]?.name && !quotedItems[selectedQuotedItemIndex]?.price && !quotedItems[selectedQuotedItemIndex]?.notes)}
                                    title="Eliminar renglón completo"
                                >
                                    <i className="bi bi-dash-circle me-1"></i>Eliminar línea
                                </Button>
                                {user?.empresa_activa?.l_sumapro !== false && (
                                    <div className="d-flex flex-row flex-lg-column gap-2">
                                        <Button variant="primary" className="fw-bold shadow-sm" onClick={() => handleAdjustQuantity(1)} disabled={selectedQuotedItemIndex === null}>+</Button>
                                        <Button variant="outline-danger" className="fw-bold shadow-sm" onClick={() => handleAdjustQuantity(-1)} disabled={selectedQuotedItemIndex === null}>-</Button>
                                    </div>
                                )}
                                <Button variant="outline-info" className="fw-bold shadow-sm px-1" style={{ fontSize: '0.8rem' }} onClick={() => setShowLiteralModal(true)}>Literal</Button>
                            </div>
                            
                            <div className="d-flex flex-row flex-lg-column gap-2 mt-auto pt-2 pt-lg-0">
                                <Button 
                                    variant="warning" 
                                    className="fw-bold shadow-sm px-2 text-dark" 
                                    style={{ fontSize: '0.85rem', minWidth: '100px' }}
                                    onClick={() => setShowSaveDraftModal(true)}
                                    disabled={!quotedItems.some(item => item.id && item.name)}
                                >
                                    Guardar
                                </Button>
                                <Button 
                                    variant="success" 
                                    className="fw-bold shadow-sm px-2" 
                                    style={{ fontSize: '0.85rem', minWidth: '100px' }}
                                    onClick={() => setShowConfirmModal(true)}
                                    disabled={!quotedItems.some(item => item.id && item.name)}
                                >
                                    Confirmar
                                </Button>
                                <Button variant="danger" className="fw-bold shadow-sm px-2" style={{ fontSize: '0.85rem', minWidth: '100px' }} onClick={() => setShowCancelModal(true)}>Cancelar</Button>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* --- VENTANAS EMERGENTES (MODALES) DE BÚSQUEDA --- */}

            {/* Modal para Buscar Clientes */}
            <ClientSearchModal
                show={showClientModal}
                onHide={handleCloseClientModal}
                onSelectClient={handleSelectClient}
                clients={dbClients}
                sellers={dbSellers}
                isLoading={isLoadingClients}
                user={user}
            />

            {/* Modal para Buscar Rubros (Categorías) de la izquierda */}
            <RubroSearchModal
                show={showRubroModal}
                onHide={() => setShowRubroModal(false)}
                rubros={rubros}
                onSelectRubro={handleSelectRubro}
            />

            {/* Modal para Buscar Artículos específicos dentro de un Rubro */}
            <ArticleSearchModal
                show={showArticleModal}
                onHide={() => setShowArticleModal(false)}
                articles={availableArticles}
                onSelectArticle={handleAddArticle}
                onOpenImageModal={handleOpenImageModal}
            />

            <GlobalArticleSearchModal
                show={showGlobalArticleModal}
                onHide={() => {
                    setShowGlobalArticleModal(false);
                    setGlobalSearchTerm('');
                }}
                onSelectArticle={handleAddArticle}
                initialSearch={globalSearchTerm}
                defaultSearchMode={user?.empresa_activa?.default_search_mode || 'codigo'}
                onOpenImageModal={handleOpenImageModal}
                pricesByArtic={pricesByArtic}
                rubros={rubros}
            />

            {/* Modal para seleccionar Lista de Precios */}
            <Modal show={showListModal} onHide={handleCloseListModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Buscar Lista de Precios</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputGroup className="mb-3">
                        <Form.Control
                            placeholder="Buscar por N° o Descripción"
                            value={listSearch}
                            onChange={(e) => {
                                setListSearch(e.target.value);
                                setListPage(1);
                            }}
                        />
                    </InputGroup>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>N° de lista</th>
                                <th>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedLists.map((l) => (
                                <tr key={l.n_lista} onClick={() => handleSelectList(l)} style={{ cursor: 'pointer' }}>
                                    <td>{l.n_lista}</td>
                                    <td>{l.d_obser || `Lista ${l.n_lista}`}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pagination>
                        {Array.from({ length: Math.ceil(filteredLists.length / itemsPerPage) }, (_, i) => (
                            <Pagination.Item key={i + 1} active={i + 1 === listPage} onClick={() => setListPage(i + 1)}>
                                {i + 1}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </Modal.Body>
            </Modal>

            {/* Literal Modal */}
            <Modal show={showLiteralModal} onHide={() => setShowLiteralModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Agregar Artículo Literal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Producto</Form.Label>
                        <Form.Control 
                            id="literal-product-input"
                            type="text" 
                            maxLength={15} 
                            placeholder="Ej: SERVICIO (por defecto LITERAL)" 
                            value={literalItem.id} 
                            onChange={e => setLiteralItem({...literalItem, id: e.target.value})} 
                            onKeyDown={e => handleLiteralKeyDown(e, 'product')}
                            className="text-uppercase fw-bold"
                            autoFocus 
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <Form.Label className="mb-0">Descripción del Artículo</Form.Label>
                            <Form.Check 
                                type="switch"
                                id="extended-desc-switch"
                                label="Desc. extendida"
                                checked={literalItem.isExtended}
                                onChange={e => setLiteralItem({...literalItem, isExtended: e.target.checked})}
                                className="small text-muted"
                            />
                        </div>
                        <Form.Control 
                            id="literal-description-input"
                            as={literalItem.isExtended ? "textarea" : "input"}
                            rows={literalItem.isExtended ? 3 : 1}
                            type="text" 
                            maxLength={literalItem.isExtended ? 255 : 55} 
                            placeholder={literalItem.isExtended ? "Escriba una descripción detallada (máx 255)..." : "Ej: Reparación de impresora (máx 55)"} 
                            value={literalItem.description} 
                            onChange={e => setLiteralItem({...literalItem, description: e.target.value})} 
                            onKeyDown={e => handleLiteralKeyDown(e, 'description')}
                        />
                        <div className="text-end small text-muted mt-1">
                            {literalItem.description.length}/{literalItem.isExtended ? 255 : 55}
                        </div>
                    </Form.Group>
                    <Row>
                        <Form.Group as={Col}>
                            <Form.Label>Precio Unitario</Form.Label>
                            <Form.Control 
                                id="literal-price-input"
                                type="number" 
                                placeholder="Ej: 5000" 
                                value={literalItem.price} 
                                onChange={e => setLiteralItem({...literalItem, price: e.target.value})} 
                                onKeyDown={e => handleLiteralKeyDown(e, 'price')}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Cantidad</Form.Label>
                            <Form.Control 
                                id="literal-quantity-input"
                                type="number" 
                                min="1" 
                                value={literalItem.quantity} 
                                onChange={e => setLiteralItem({...literalItem, quantity: e.target.value})} 
                                onKeyDown={e => handleLiteralKeyDown(e, 'quantity')}
                            />
                        </Form.Group>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowLiteralModal(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={handleAddLiteral}>Aceptar</Button>
                </Modal.Footer>
            </Modal>
            {/* Confirmar Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5"><i className="bi bi-check-circle me-2 text-success"></i>Confirmar</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <p className="mb-0 fw-bold fs-5">¿Desea confirmar esta operación?</p>
                    <div className="text-muted small mt-2">Monto: {formatCurrency(finalGrandTotal)}</div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center border-0 pb-4">
                    <Button variant="secondary" size="sm" onClick={() => setShowConfirmModal(false)} className="px-3" disabled={isSaving}>Cancelar</Button>
                    <Button
                        variant="warning"
                        size="sm"
                        onClick={() => {
                            setShowConfirmModal(false);
                            setShowSaveDraftModal(true);
                        }}
                        className="px-3 fw-bold text-dark"
                        disabled={isSaving}
                    >
                        Guardar Presupuesto
                    </Button>
                    <Button 
                        variant="success" 
                        size="sm" 
                        onClick={() => {
                            setShowConfirmModal(false);
                            setShowFinalConfirmModal(true);
                        }}
                        className="px-3"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Guardando...' : 'Confirmar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/*Segundo Modal de Confirmación Definitiva (APB)*/}
            <Modal show={showFinalConfirmModal} onHide={() => setShowFinalConfirmModal(false)} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5 text-success"><i className="bi bi-shield-check me-2"></i>Confirmar Presupuesto</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <p className="mb-0 fw-bold fs-5">¿Estás seguro que deseas confirmar el presupuesto?</p>
                    <div className="text-muted small mt-2">Una vez confirmado se generará el comprobante definitivo.</div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center border-0 pb-4">
                    <Button variant="secondary" size="sm" onClick={() => setShowFinalConfirmModal(false)} className="px-3" disabled={isSaving}>Cancelar</Button>
                    <Button
                        variant="success"
                        size="sm"
                        onClick={handleSaveBudget}
                        className="px-3 fw-bold"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Confirmando...' : 'Confirmar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/*Modal de Confirmación para Guardar Borrador (APB)*/}
            <Modal show={showSaveDraftModal} onHide={() => setShowSaveDraftModal(false)} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5 text-warning"><i className="bi bi-save me-2 text-dark"></i>Guardar Borrador</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <p className="mb-0 fw-bold fs-5">¿Estás seguro que deseas guardar este presupuesto como borrador?</p>
                    <div className="text-muted small mt-2">Podrás retomarlo y editarlo más tarde desde la Consulta de Presupuestos.</div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center border-0 pb-4">
                    <Button variant="secondary" size="sm" onClick={() => setShowSaveDraftModal(false)} className="px-3" disabled={isSaving}>Cancelar</Button>
                    <Button
                        variant="warning"
                        size="sm"
                        onClick={handleSaveDraft}
                        className="px-3 fw-bold text-dark"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Cancelación */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5 text-danger"><i className="bi bi-exclamation-triangle me-2"></i>Cancelar</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <p className="mb-0">¿Está seguro que desea cancelar?</p>
                    <div className="text-muted small mt-2">Se borrarán todos los datos cargados.</div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center border-0 pb-4">
                    <Button variant="secondary" size="sm" onClick={() => setShowCancelModal(false)} className="px-3">No, continuar</Button>
                    <Button variant="danger" size="sm" onClick={handleReset} className="px-3">Sí, resetear</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Dirección de Entrega */}
            <Modal show={showDeliveryAddressModal} onHide={() => setShowDeliveryAddressModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5">
                        <i className="bi bi-truck me-2 text-primary"></i>Dirección de Entrega
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">
                        Seleccione la dirección de entrega para este presupuesto o ingrese una manualmente:
                    </p>
                    <div className="d-flex flex-column gap-2">
                        {/* Opción 1: Domicilio Fiscal (Preferente por defecto) */}
                        <div
                            className={`p-2 rounded border ${tempDeliveryOption === 'fiscal' ? 'border-primary bg-primary-subtle' : 'bg-light'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setTempDeliveryOption('fiscal')}
                        >
                            <Form.Check
                                type="radio"
                                id="delivery-opt-fiscal"
                                name="deliveryAddressOption"
                                checked={tempDeliveryOption === 'fiscal'}
                                onChange={() => setTempDeliveryOption('fiscal')}
                                label={
                                    <div>
                                        <span className="fw-bold d-block">Domicilio Fiscal (Por defecto)</span>
                                        <span className="text-muted small">{client.address || 'Sin domicilio fiscal registrado'}</span>
                                    </div>
                                }
                            />
                        </div>

                        {/* Opción 2: Dirección Alternativa 1 (MCLI000.D_ENTRE1) */}
                        <div
                            className={`p-2 rounded border ${!client.d_entre1 ? 'opacity-50 bg-light' : (tempDeliveryOption === 'alt1' ? 'border-primary bg-primary-subtle' : 'bg-light')}`}
                            style={{ cursor: client.d_entre1 ? 'pointer' : 'not-allowed' }}
                            onClick={() => { if (client.d_entre1) setTempDeliveryOption('alt1'); }}
                        >
                            <Form.Check
                                type="radio"
                                id="delivery-opt-alt1"
                                name="deliveryAddressOption"
                                disabled={!client.d_entre1}
                                checked={tempDeliveryOption === 'alt1'}
                                onChange={() => { if (client.d_entre1) setTempDeliveryOption('alt1'); }}
                                label={
                                    <div>
                                        <span className="fw-bold d-block">Dirección Alternativa 1</span>
                                        <span className="text-muted small">{client.d_entre1 || 'Sin dirección alternativa 1 registrada'}</span>
                                    </div>
                                }
                            />
                        </div>

                        {/* Opción 3: Dirección Alternativa 2 (MCLI000.D_ENTRE2) */}
                        <div
                            className={`p-2 rounded border ${!client.d_entre2 ? 'opacity-50 bg-light' : (tempDeliveryOption === 'alt2' ? 'border-primary bg-primary-subtle' : 'bg-light')}`}
                            style={{ cursor: client.d_entre2 ? 'pointer' : 'not-allowed' }}
                            onClick={() => { if (client.d_entre2) setTempDeliveryOption('alt2'); }}
                        >
                            <Form.Check
                                type="radio"
                                id="delivery-opt-alt2"
                                name="deliveryAddressOption"
                                disabled={!client.d_entre2}
                                checked={tempDeliveryOption === 'alt2'}
                                onChange={() => { if (client.d_entre2) setTempDeliveryOption('alt2'); }}
                                label={
                                    <div>
                                        <span className="fw-bold d-block">Dirección Alternativa 2</span>
                                        <span className="text-muted small">{client.d_entre2 || 'Sin dirección alternativa 2 registrada'}</span>
                                    </div>
                                }
                            />
                        </div>

                        {/* Opción 4: Ingresar manualmente */}
                        <div
                            className={`p-2 rounded border ${tempDeliveryOption === 'manual' ? 'border-primary bg-primary-subtle' : 'bg-light'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setTempDeliveryOption('manual')}
                        >
                            <Form.Check
                                type="radio"
                                id="delivery-opt-manual"
                                name="deliveryAddressOption"
                                checked={tempDeliveryOption === 'manual'}
                                onChange={() => setTempDeliveryOption('manual')}
                                label={<span className="fw-bold">Ingresar dirección manualmente</span>}
                            />
                            <div className="mt-2 ps-4">
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    maxLength={50}
                                    placeholder="Escriba la dirección de entrega (máx. 50 caracteres)..."
                                    value={tempManualAddress || ''}
                                    onFocus={() => setTempDeliveryOption('manual')}
                                    onChange={(e) => {
                                        setTempDeliveryOption('manual');
                                        setTempManualAddress(e.target.value);
                                    }}
                                />
                                <div className={`text-end small mt-1 ${(tempManualAddress || '').length >= 50 ? 'text-danger fw-bold' : 'text-muted'}`}>
                                    {(tempManualAddress || '').length}/50
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowDeliveryAddressModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                            if (tempDeliveryOption === 'manual' && !tempManualAddress.trim()) {
                                Swal.fire({
                                    title: 'Atención',
                                    text: 'Por favor ingrese una dirección manual o seleccione una de las direcciones del cliente.',
                                    icon: 'warning',
                                    confirmButtonColor: '#3085d6',
                                    confirmButtonText: 'Aceptar'
                                });
                                return;
                            }
                            setDeliveryAddressOption(tempDeliveryOption);
                            setManualDeliveryAddress(tempDeliveryOption === 'manual' ? tempManualAddress.trim() : '');
                            if (tempDeliveryOption === 'fiscal') {
                                setDeliveryAddress(client.address || '');
                            } else if (tempDeliveryOption === 'alt1') {
                                setDeliveryAddress(client.d_entre1 || '');
                            } else if (tempDeliveryOption === 'alt2') {
                                setDeliveryAddress(client.d_entre2 || '');
                            } else if (tempDeliveryOption === 'manual') {
                                setDeliveryAddress(tempManualAddress.trim().substring(0, 50));
                            }
                            setShowDeliveryAddressModal(false);
                        }}
                    >
                        Aceptar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* General Note Modal */}
            <Modal show={showGeneralNoteModal} onHide={() => setShowGeneralNoteModal(false)} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5"><i className="bi bi-pencil-square me-2"></i>Nota General</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label className="text-muted small">Nota del Presupuesto (máx. 60 caracteres)</Form.Label>
                        <Form.Control 
                            as="textarea"
                            rows={3}
                            maxLength={60}
                            value={generalNote}
                            onChange={(e) => setGeneralNote(e.target.value)}
                            placeholder="Escribe una nota aquí..."
                            autoFocus
                        />
                        <div className={`text-end small mt-1 ${generalNote.length >= 60 ? 'text-danger fw-bold' : 'text-muted'}`}>
                            {generalNote.length}/60
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" size="sm" onClick={() => setShowGeneralNoteModal(false)}>Aceptar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Confirmación para Eliminar Renglón */}
            <Modal show={showDeleteRowModal} onHide={() => setShowDeleteRowModal(false)} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5 text-danger"><i className="bi bi-trash me-2"></i>Eliminar Renglón</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <p className="mb-0 fw-bold">¿Está seguro que desea eliminar este renglón?</p>
                    <div className="text-muted small mt-2">Esta acción vaciará o removerá el renglón seleccionado.</div>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center border-0 pb-4">
                    <Button variant="secondary" size="sm" onClick={() => setShowDeleteRowModal(false)} className="px-3">No</Button>
                    <Button variant="danger" size="sm" onClick={confirmDeleteRow} className="px-3">Sí, Eliminar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Advertencia: Cliente no asignado */}
            <Modal show={unassignedClientError} onHide={handleCloseUnassignedError} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5 text-warning">
                        <i className="bi bi-exclamation-triangle me-2"></i>Aviso
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <p className="mb-0 fw-bold">Cliente no asignado al vendedor</p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center border-0 pb-4">
                    <Button variant="primary" size="sm" onClick={handleCloseUnassignedError} className="px-4">
                        Aceptar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Imagen de Artículo */}
            <ArticleImageModal 
                show={showImageModal} 
                onHide={() => setShowImageModal(false)} 
                article={selectedImageArticle} 
                baseImageUrl={user?.empresa_activa?.d_undisky}
            />

            {/* Modal de Éxito / Compartir Presupuesto (WhatsApp, Email, Imprimir, Descargar) */}
            <QuoteSuccessModal
                show={showSuccessModal}
                onHide={() => {
                    setShowSuccessModal(false);
                    handleReset();
                }}
                quote={savedQuoteData}
                user={user}
                sellers={dbSellers}
                onDone={() => {
                    setShowSuccessModal(false);
                    handleReset();
                }}
            />

        </div>
    );
};

export default Quoter;
